<template>
    <div class="cloud-home relative h-screen-header overflow-hidden">
        <div class="cloud-home-shell relative flex h-full min-h-0 flex-col">
            <main class="flex min-h-0 flex-1 flex-col p-3 md:p-4 xl:p-5">
                <header class="cloud-workbar mb-3 rounded-2xl border px-3 py-3 md:px-4">
                    <div class="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div class="flex min-w-0 items-center gap-3">
                            <button class="btn btn-circle btn-ghost btn-sm shrink-0" aria-label="返回上级目录" @click="goBack">
                                <svg viewBox="0 0 24 24" fill="none" class="h-5 w-5">
                                    <path
                                        d="m15 18-6-6 6-6"
                                        stroke="currentColor"
                                        stroke-width="1.8"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                            <div class="min-w-0">
                                <p class="text-[11px] font-semibold text-base-content/45">云端文件</p>
                                <div class="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-base-content/60">
                                <span
                                    v-for="(item, index) in breadcrumbs"
                                    :key="`${item.path}-${index}`"
                                    class="inline-flex min-w-0 items-center"
                                >
                                    <button
                                        class="truncate transition hover:text-primary"
                                        type="button"
                                        @click="navigateToPath(item.path)"
                                    >
                                        {{ item.name }}
                                    </button>
                                    <span v-if="index !== breadcrumbs.length - 1" class="mx-1 text-base-content/30">/</span>
                                </span>
                                </div>
                            </div>
                        </div>
                        <button
                            class="btn btn-sm h-10 min-h-10 rounded-xl border border-base-300/70 bg-base-100 px-4 shadow-none hover:border-primary/30 hover:bg-primary/5"
                            type="button"
                            @click="openTopFileIndexer"
                        >
                            <svg-icon name="file-index" class="mr-2 h-4 w-4 fill-current" />
                            文件索引
                        </button>
                    </div>
                </header>

                <section class="cloud-workspace flex min-h-0 min-w-0 flex-1 flex-col rounded-2xl border px-3 py-4 md:px-4 md:py-4 xl:px-5">
                    <div class="flex flex-col gap-3">
                        <div class="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                            <div class="min-w-0">
                                <div class="cloud-tabs inline-flex flex-wrap items-center gap-1 rounded-xl p-1">
                                    <button
                                        v-for="tab in tabs"
                                        :key="tab"
                                        class="rounded-lg px-4 py-2 text-[14px] font-semibold transition"
                                        :class="activeTab === tab
                                            ? 'bg-base-100 text-primary shadow-sm'
                                            : 'text-base-content/60 hover:bg-base-100/70 hover:text-base-content'"
                                        type="button"
                                        @click="activeTab = tab"
                                    >
                                        {{ tab }}
                                    </button>
                                </div>
                            </div>
                            <div class="flex flex-wrap items-center gap-2">
                                <label class="cloud-search input flex h-11 w-full items-center gap-3 rounded-xl border shadow-none outline-none md:w-[360px] xl:w-[420px]">
                                    <svg viewBox="0 0 24 24" fill="none" class="h-5 w-5 opacity-55">
                                        <path
                                            d="M21 21l-4.35-4.35M10.8 18a7.2 7.2 0 1 0 0-14.4 7.2 7.2 0 0 0 0 14.4Z"
                                            stroke="currentColor"
                                            stroke-width="1.8"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                    <input
                                        v-model="localSearchWord"
                                        class="focus:outline-none"
                                        type="text"
                                        placeholder="搜索文件、专辑、艺术家，或输入路径"
                                        @input="handleSearchInput"
                                        @keyup.enter="applySearch"
                                    />
                                    <button
                                        v-if="localSearchWord"
                                        class="btn btn-ghost btn-xs btn-circle"
                                        type="button"
                                        @click="clearSearch"
                                    >
                                        ✕
                                    </button>
                                </label>
                                <div class="dropdown dropdown-end">
                                    <button tabindex="0" class="cloud-tool-button btn btn-sm h-10 min-h-10 rounded-xl px-4 shadow-none" type="button">
                                        筛选
                                    </button>
                                    <div tabindex="0" class="dropdown-content z-20 mt-2 w-56 rounded-2xl border border-base-300/80 bg-base-100 p-2 shadow-xl">
                                        <button
                                            v-for="option in filterOptions"
                                            :key="option.id"
                                            class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition hover:bg-primary/8"
                                            :class="selectedFilterId === option.id ? 'bg-primary/10 text-primary' : 'text-base-content'"
                                            type="button"
                                            @click="selectFilterOption(option)"
                                        >
                                            <span>{{ option.label }}</span>
                                            <span v-if="selectedFilterId === option.id" class="text-xs font-semibold">已选</span>
                                        </button>
                                        <button
                                            v-if="selectedFilterId"
                                            class="mt-1 flex w-full items-center rounded-xl px-3 py-2 text-left text-sm text-base-content/65 transition hover:bg-base-200"
                                            type="button"
                                            @click="clearSelectedFilter"
                                        >
                                            清除筛选
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                            <div class="flex flex-wrap items-center gap-2 text-sm">
                                <div class="cloud-chip inline-flex items-center rounded-xl px-3 py-2 text-base-content/65">
                                    当前视图共 <span class="ml-1 font-semibold text-base-content/80">{{ currentViewEntries.length }}</span> 项
                                </div>
                                <div
                                    v-if="selectedCount > 0"
                                    class="cloud-chip inline-flex items-center rounded-xl px-3 py-2 text-base-content/65"
                                >
                                    已选 <span class="ml-1 font-semibold text-base-content/80">{{ selectedCount }}</span> 项
                                </div>
                                <div
                                    v-if="selectedFilterOption"
                                    class="inline-flex items-center gap-2 rounded-xl bg-primary/10 px-3 py-2 text-primary"
                                >
                                    <span>{{ selectedFilterOption.label }}</span>
                                    <button class="btn btn-ghost btn-xs btn-circle min-h-0 h-6 w-6" type="button" @click="clearSelectedFilter">
                                        ✕
                                    </button>
                                </div>
                                <div
                                    v-for="chip in activeMetaFilterChips"
                                    :key="chip.key"
                                    class="cloud-chip inline-flex items-center gap-2 rounded-xl px-3 py-2 text-base-content/75"
                                >
                                    <span>{{ chip.label }}</span>
                                    <button class="btn btn-ghost btn-xs btn-circle min-h-0 h-6 w-6" type="button" @click="clearMetaFilter(chip.key)">
                                        ✕
                                    </button>
                                </div>
                                <div
                                    v-if="sortSummary"
                                    class="cloud-chip inline-flex max-w-full items-center rounded-xl px-3 py-2 text-base-content/65"
                                >
                                    <span class="truncate">排序：{{ sortSummary }}</span>
                                    <button class="btn btn-ghost btn-xs btn-circle ml-1 min-h-0 h-6 w-6 shrink-0" type="button" @click="clearSortedFields">
                                        ✕
                                    </button>
                                </div>
                                <div
                                    v-if="dragStatusLabel"
                                    class="inline-flex items-center rounded-xl bg-primary/10 px-3 py-2 text-primary"
                                >
                                    {{ dragStatusLabel }}
                                </div>
                            </div>

                            <div class="flex flex-wrap items-center gap-2">
                                <div class="cloud-tool-button inline-flex h-10 items-center gap-2 rounded-xl px-3 text-sm text-base-content/75">
                                    <label class="cloud-check">
                                        <input
                                            :checked="allSelected"
                                            type="checkbox"
                                            @change="toggleAllSelection"
                                        />
                                        <span></span>
                                    </label>
                                    <span>全选</span>
                                </div>
                                <div class="dropdown dropdown-end">
                                    <button tabindex="0" class="cloud-tool-button btn btn-sm h-10 min-h-10 rounded-xl px-4 shadow-none" type="button">
                                        排序
                                    </button>
                                    <div tabindex="0" class="dropdown-content z-20 mt-2 w-64 rounded-2xl border border-base-300/80 bg-base-100 p-2 shadow-xl">
                                        <button
                                            v-for="option in sortOptions"
                                            :key="option.id"
                                            class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition hover:bg-primary/8"
                                            :class="getSortStatus(option.id) ? 'bg-primary/10 text-primary' : 'text-base-content'"
                                            type="button"
                                            @click="changeSorted(option.id)"
                                        >
                                            <span>{{ option.label }}</span>
                                            <span v-if="getSortStatus(option.id) === 'asc'" class="text-xs font-semibold">升序</span>
                                            <span v-else-if="getSortStatus(option.id) === 'desc'" class="text-xs font-semibold">降序</span>
                                        </button>
                                        <button
                                            v-if="sortedField.length > 0"
                                            class="mt-1 flex w-full items-center rounded-xl px-3 py-2 text-left text-sm text-base-content/65 transition hover:bg-base-200"
                                            type="button"
                                            @click="clearSortedFields"
                                        >
                                            清除排序
                                        </button>
                                    </div>
                                </div>
                                <div v-if="viewMode === 'list'" class="dropdown dropdown-end">
                                    <button tabindex="0" class="cloud-tool-button btn btn-sm h-10 min-h-10 rounded-xl px-4 shadow-none" type="button">
                                        列
                                    </button>
                                    <div tabindex="0" class="dropdown-content z-20 mt-2 w-[min(92vw,440px)] rounded-2xl border border-base-300/80 bg-base-100 p-3 shadow-xl">
                                        <div class="cloud-column-picker-grid">
                                            <label
                                                v-for="column in homeCloudListFieldOptions"
                                                :key="column.id"
                                                class="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-base-200/80 bg-base-200/20 px-3 py-2 text-sm transition hover:border-primary/25 hover:bg-primary/8"
                                            >
                                                <span class="min-w-0 truncate">{{ column.label }}</span>
                                                <input
                                                    class="checkbox checkbox-sm shrink-0"
                                                    :checked="isListColumnVisible(column.id)"
                                                    type="checkbox"
                                                    @change="toggleListColumn(column.id)"
                                                />
                                            </label>
                                        </div>
                                        <button
                                            class="mt-3 flex w-full items-center justify-center rounded-xl bg-base-200/70 px-3 py-2 text-sm text-base-content/70 transition hover:bg-base-200"
                                            type="button"
                                            @click="resetListColumns"
                                        >
                                            恢复默认
                                        </button>
                                    </div>
                                </div>
                                <div class="join rounded-xl border border-base-300/70 bg-base-200/60 p-1">
                                    <button
                                        class="btn btn-sm join-item rounded-xl border-0"
                                        :class="viewMode === 'list' ? 'btn-neutral text-neutral-content' : 'btn-ghost'"
                                        type="button"
                                        @click="switchViewMode('list')"
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                                            <path
                                                d="M8 6h11M8 12h11M8 18h11M4 6h.01M4 12h.01M4 18h.01"
                                                stroke="currentColor"
                                                stroke-width="1.8"
                                                stroke-linecap="round"
                                            />
                                        </svg>
                                    </button>
                                    <button
                                        class="btn btn-sm join-item rounded-xl border-0"
                                        :class="viewMode === 'card' ? 'btn-neutral text-neutral-content' : 'btn-ghost'"
                                        type="button"
                                        @click="switchViewMode('card')"
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                                            <path
                                                d="M4 4h6v6H4V4Zm10 0h6v6h-6V4ZM4 14h6v6H4v-6Zm10 0h6v6h-6v-6Z"
                                                stroke="currentColor"
                                                stroke-width="1.8"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </button>
                                </div>
                                <button class="cloud-icon-button btn btn-square btn-ghost btn-sm rounded-xl" aria-label="刷新当前目录" @click="refreshCurrentView">
                                    <svg viewBox="0 0 24 24" fill="none" class="h-5 w-5">
                                        <path
                                            d="M20 12a8 8 0 1 1-2.34-5.66M20 4v6h-6"
                                            stroke="currentColor"
                                            stroke-width="1.8"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        class="cloud-content mt-4 min-h-0 min-w-0 flex-1 pr-1"
                        :class="viewMode === 'list' ? 'overflow-auto' : 'overflow-y-auto overflow-x-hidden'"
                    >
                        <div v-if="isLoading" class="flex min-h-[320px] items-center justify-center">
                            <span class="loading loading-spinner loading-lg text-primary"></span>
                        </div>

                        <div v-else-if="loadError" class="cloud-state-panel border-error/30 bg-error/5 p-6 text-center">
                            <p class="text-sm font-semibold text-error">{{ loadError }}</p>
                            <button class="btn btn-sm btn-outline btn-error mt-4 rounded-full" @click="refreshCurrentView">
                                重试
                            </button>
                        </div>

                        <div v-else-if="currentViewEntries.length === 0" class="cloud-state-panel p-10 text-center">
                            <p class="text-base font-semibold text-base-content/70">当前目录暂无文件</p>
                            <p class="mt-2 text-sm text-base-content/45">可以切换目录、清空搜索词，或刷新索引后再试。</p>
                        </div>

                        <div
                            v-else-if="viewMode === 'card'"
                            class="cloud-card-grid grid grid-cols-[repeat(auto-fill,minmax(132px,1fr))] gap-x-4 gap-y-5"
                        >
                            <article
                                v-for="item in visibleEntries"
                                :key="item.id"
                                class="group relative"
                                @pointerenter="handleSelectionPointerEnter(item)"
                            >
                                <label
                                    class="cloud-floating-check transition-opacity duration-150"
                                    :class="isDragSelecting || isSelected(item.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
                                    @pointerdown="handleSelectionPointerDown(item, $event)"
                                >
                                    <input
                                        :checked="isSelected(item.id)"
                                        type="checkbox"
                                        @click.prevent.stop
                                    />
                                    <span></span>
                                </label>

                                <div
                                    class="cloud-file-card flex w-full flex-col items-center text-center"
                                    :class="isInDragPreview(item.id) ? 'bg-primary/5 ring-1 ring-primary/25' : ''"
                                >
                                    <div
                                        class="flex w-full flex-col items-center text-center"
                                        role="button"
                                        tabindex="0"
                                        @click="handlePrimaryAction(item)"
                                        @keydown.enter.prevent="handlePrimaryAction(item)"
                                        @keydown.space.prevent="handlePrimaryAction(item)"
                                    >
                                        <div
                                            class="relative flex h-20 w-20 items-center justify-center rounded-2xl transition group-hover:scale-[1.03]"
                                            :class="iconToneClass(item)"
                                        >
                                            <svg v-if="item.kind === 'folder'" viewBox="0 0 24 24" fill="none" class="h-20 w-20 drop-shadow-sm">
                                                <path
                                                    d="M3 7.5A2.5 2.5 0 0 1 5.5 5H9l1.6 1.8c.29.32.7.5 1.13.5H18.5A2.5 2.5 0 0 1 21 9.8v6.7A2.5 2.5 0 0 1 18.5 19H5.5A2.5 2.5 0 0 1 3 16.5v-9Z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                            <svg v-else-if="item.kind === 'image'" viewBox="0 0 24 24" fill="none" class="h-16 w-16 opacity-90">
                                                <path
                                                    d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm0 11 3.5-3.5 2.5 2.5 4-4L20 15"
                                                    stroke="currentColor"
                                                    stroke-width="1.8"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                                <circle cx="9" cy="8" r="1.4" fill="currentColor" />
                                            </svg>
                                            <svg v-else-if="item.kind === 'audio'" viewBox="0 0 24 24" fill="none" class="h-16 w-16 opacity-85">
                                                <path
                                                    d="M7 3.75A1.75 1.75 0 0 1 8.75 2h5.69c.46 0 .9.18 1.23.5l3.83 3.83c.32.33.5.77.5 1.23v12.69A1.75 1.75 0 0 1 18.25 22h-9.5A1.75 1.75 0 0 1 7 20.25V3.75Z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                            <svg v-else viewBox="0 0 24 24" fill="none" class="h-16 w-16 opacity-85">
                                                <path
                                                    d="M7 3.75A1.75 1.75 0 0 1 8.75 2h5.69c.46 0 .9.18 1.23.5l3.83 3.83c.32.33.5.77.5 1.23v12.69A1.75 1.75 0 0 1 18.25 22h-9.5A1.75 1.75 0 0 1 7 20.25V3.75Z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                            <button
                                                v-if="item.kind === 'audio' && isPlayableAudioItem(item)"
                                                class="absolute -bottom-0.5 right-0 flex items-center justify-center p-1 text-primary transition hover:scale-105 hover:text-primary/85"
                                                type="button"
                                                aria-label="播放"
                                                @click.stop="playAudioItem(item)"
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4 drop-shadow-sm">
                                                    <path d="M8 6.5v11l9-5.5-9-5.5Z" />
                                                </svg>
                                            </button>
                                        </div>

                                        <div
                                            class="mt-2 w-full rounded-xl px-1 py-1 transition"
                                            :class="isSelected(item.id) ? 'bg-primary/10' : ''"
                                        >
                                            <p class="line-clamp-2 text-[14px] font-semibold leading-5 text-base-content" :title="item.title">
                                                {{ item.title }}
                                            </p>
                                            <p class="mt-1 text-xs text-base-content/50">{{ item.sizeLabel }}</p>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        </div>

                        <div v-else class="space-y-2">
                            <div class="cloud-list-scroll overflow-x-auto pb-1">
                                <div class="cloud-list-table">
                                    <div
                                        class="cloud-list-head items-center gap-3 rounded-xl px-4 py-3 text-xs font-bold text-base-content/50 max-[720px]:hidden"
                                        :style="{ '--cloud-list-grid-template': listGridTemplateColumns }"
                                    >
                                        <span></span>
                                        <span v-for="column in visibleListColumns" :key="column.id">{{ column.label }}</span>
                                        <span>操作</span>
                                    </div>

                                    <article
                                        v-for="item in visibleListEntries"
                                        :key="item.id"
                                        class="cloud-list-row group items-center gap-3 rounded-xl border px-4 py-3"
                                        :style="{ '--cloud-list-grid-template': listGridTemplateColumns }"
                                        :class="isSelected(item.id)
                                            ? 'border-primary/40 bg-primary/10 shadow-sm'
                                            : (isInDragPreview(item.id)
                                                ? 'border-primary/30 bg-primary/5 shadow-sm'
                                            : 'border-transparent')"
                                        @pointerenter="handleSelectionPointerEnter(item)"
                                    >
                                        <div class="max-[720px]:order-[-1]">
                                            <label
                                                class="cloud-check transition-opacity duration-150"
                                                :class="isDragSelecting || isSelected(item.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
                                                @pointerdown="handleSelectionPointerDown(item, $event)"
                                            >
                                                <input
                                                    :checked="isSelected(item.id)"
                                                    type="checkbox"
                                                    @click.prevent.stop
                                                />
                                                <span></span>
                                            </label>
                                        </div>

                                        <template v-for="column in visibleListColumns">
                                            <div v-if="column.id === 'album_img'" :key="`${item.id}-${column.id}`" class="flex items-center">
                                                <button
                                                    class="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-base-200/70"
                                                    type="button"
                                                    @click="item.artwork ? openImagePreview(item) : null"
                                                >
                                                    <img
                                                        v-if="item.artwork"
                                                        :src="item.artwork"
                                                        alt="cover"
                                                        class="h-full w-full object-cover"
                                                    />
                                                    <div v-else class="text-xs text-base-content/40">-</div>
                                                </button>
                                            </div>
                                            <div v-else-if="column.id === 'title'" :key="`${item.id}-${column.id}`" class="min-w-0">
                                                <div class="flex min-w-0 items-center gap-3">
                                                    <div
                                                        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                                                        :class="iconToneClass(item)"
                                                    >
                                                        <svg v-if="item.kind === 'folder'" viewBox="0 0 24 24" fill="none" class="h-10 w-10">
                                                            <path
                                                                d="M3 7.5A2.5 2.5 0 0 1 5.5 5H9l1.6 1.8c.29.32.7.5 1.13.5H18.5A2.5 2.5 0 0 1 21 9.8v6.7A2.5 2.5 0 0 1 18.5 19H5.5A2.5 2.5 0 0 1 3 16.5v-9Z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                        <svg v-else-if="item.kind === 'image'" viewBox="0 0 24 24" fill="none" class="h-8 w-8">
                                                            <path
                                                                d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm0 11 3.5-3.5 2.5 2.5 4-4L20 15"
                                                                stroke="currentColor"
                                                                stroke-width="1.8"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                            />
                                                            <circle cx="9" cy="8" r="1.4" fill="currentColor" />
                                                        </svg>
                                                        <svg v-else-if="item.kind === 'audio'" viewBox="0 0 24 24" fill="none" class="h-8 w-8">
                                                            <path
                                                                d="M7 3.75A1.75 1.75 0 0 1 8.75 2h5.69c.46 0 .9.18 1.23.5l3.83 3.83c.32.33.5.77.5 1.23v12.69A1.75 1.75 0 0 1 18.25 22h-9.5A1.75 1.75 0 0 1 7 20.25V3.75Z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                        <svg v-else viewBox="0 0 24 24" fill="none" class="h-8 w-8">
                                                            <path
                                                                d="M7 3.75A1.75 1.75 0 0 1 8.75 2h5.69c.46 0 .9.18 1.23.5l3.83 3.83c.32.33.5.77.5 1.23v12.69A1.75 1.75 0 0 1 18.25 22h-9.5A1.75 1.75 0 0 1 7 20.25V3.75Z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                    </div>

                                                    <div class="min-w-0">
                                                        <button class="truncate text-left text-sm font-semibold text-base-content hover:text-primary" @click="handlePrimaryAction(item)">
                                                            {{ item.title }}
                                                        </button>
                                                        <p v-if="!isListColumnVisible('filename')" class="mt-1 truncate text-xs text-base-content/55">{{ item.fileName }}</p>
                                                        <p v-if="item.relativeDir" class="mt-1 truncate text-[11px] text-base-content/40">{{ item.relativeDir }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-else-if="column.id === 'lyrics' || column.id === 'lyrics_file'" :key="`${item.id}-${column.id}`">
                                                <span
                                                    class="badge badge-sm border-0"
                                                    :class="hasListColumnValue(item, column.id) ? 'badge-success text-success-content' : 'badge-ghost'"
                                                >
                                                    {{ hasListColumnValue(item, column.id) ? '有' : '无' }}
                                                </span>
                                            </div>
                                            <div v-else-if="column.id === 'status'" :key="`${item.id}-${column.id}`">
                                                <span class="badge badge-sm border-0" :class="badgeClass(item.statusTone)">
                                                    {{ item.status }}
                                                </span>
                                            </div>
                                            <div v-else :key="`${item.id}-${column.id}`" class="truncate text-sm text-base-content/70">
                                                {{ getListColumnDisplayValue(item, column.id) }}
                                            </div>
                                        </template>
                                        <div class="flex items-center gap-2">
                                            <button
                                                v-if="item.kind === 'audio' && isPlayableAudioItem(item)"
                                                class="btn btn-xs rounded-full border-0 bg-primary/12 px-3 text-primary hover:bg-primary/18"
                                                type="button"
                                                @click.stop="playAudioItem(item)"
                                            >
                                                播放
                                            </button>
                                            <button
                                                class="btn btn-xs btn-ghost rounded-full"
                                                type="button"
                                                @click.stop="handleRowAction(item)"
                                            >
                                                {{ getRowActionLabel(item) }}
                                            </button>
                                        </div>
                                    </article>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="listEntries.length > 0"
                            class="cloud-pagination mt-4 flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                            <div class="text-sm text-base-content/60">
                                已加载 <span class="font-semibold text-base-content">{{ listEntries.length }}</span>
                                / {{ totalCount || listEntries.length }} 项
                            </div>

                            <div class="flex flex-wrap items-center gap-3">
                                <label class="flex items-center gap-2 text-sm text-base-content/60">
                                    <span>每页</span>
                                    <select
                                        class="select select-bordered select-sm rounded-xl"
                                        :value="pageSize"
                                        @change="handlePageSizeChange(Number($event.target.value))"
                                    >
                                        <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                                    </select>
                                </label>

                                <div class="text-sm text-base-content/60">
                                    <span v-if="isLoadingMore" class="inline-flex items-center gap-2">
                                        <span class="loading loading-spinner loading-xs"></span>
                                        正在加载更多
                                    </span>
                                    <span v-else-if="hasMoreData">滚动到底部自动加载</span>
                                    <span v-else>没有更多了</span>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="listEntries.length > 0"
                            ref="loadMoreSentinel"
                            class="h-2 w-full"
                        ></div>
                    </div>
                </section>
            </main>
        </div>

        <transition name="cloud-drawer-fade">
            <div v-if="activeItem" class="fixed inset-0 z-30 bg-base-content/20" @click="closeInspector"></div>
        </transition>

        <transition name="cloud-drawer-slide">
            <aside
                v-if="activeItem"
                class="fixed right-0 top-0 z-40 h-screen w-full overflow-y-auto border-l border-base-200 bg-base-100/95 p-6 shadow-2xl backdrop-blur md:w-[420px]"
            >
                <div class="flex items-start justify-between gap-3">
                    <div>
                        <p class="mb-1 text-xs font-bold text-base-content/45">属性面板</p>
                        <h2 class="text-2xl font-extrabold text-base-content">{{ activeItem.title }}</h2>
                        <p class="mt-2 text-sm text-base-content/55">{{ activeItem.fileName }}</p>
                    </div>
                    <button class="btn btn-sm btn-circle btn-ghost" type="button" aria-label="关闭详情" @click="closeInspector">
                        <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                            <path
                                d="M18 6 6 18M6 6l12 12"
                                stroke="currentColor"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>
                <div class="mt-5">
                    <div v-if="detailLoading" class="flex min-h-[320px] items-center justify-center">
                        <span class="loading loading-spinner loading-lg text-primary"></span>
                    </div>

                    <div v-else-if="detailError" class="rounded-2xl border border-error/30 bg-error/5 p-6 text-center">
                        <p class="text-sm font-semibold text-error">{{ detailError }}</p>
                        <button class="btn btn-sm btn-outline btn-error mt-4 rounded-full" @click="retryLoadDetail">
                            重试
                        </button>
                    </div>

                    <music-meta-data
                        v-else-if="detailMusicInfo && Object.keys(detailMusicInfo).length > 0"
                        :loading="detailLoading"
                        :music-info="detailMusicInfo"
                        :reload-img="reloadImg"
                        :drawer-mode="'drawer'"
                        :unique-artist-options="uniqueArtistOptions"
                        :unique-album-options="uniqueAlbumOptions"
                        :unique-year-options="uniqueYearOptions"
                        :unique-genre-options="uniqueGenreOptions"
                        :unique-artwork-options="uniqueArtworkOptions"
                        @show-version-history="handleShowVersionHistory"
                    />
                </div>
            </aside>
        </transition>

        <transition name="cloud-drawer-fade">
            <div v-if="isShowResourceId3" class="fixed inset-0 z-40 bg-base-content/20" @click="isShowResourceId3 = false"></div>
        </transition>

        <transition name="cloud-drawer-slide">
            <aside
                v-if="isShowResourceId3"
                class="fixed right-0 top-0 z-50 h-screen w-full overflow-y-auto border-l border-base-200 bg-base-100/95 p-4 shadow-2xl backdrop-blur md:w-[800px]"
            >
                <div class="flex items-center justify-between gap-3 border-b border-base-200/80 pb-4">
                    <div>
                        <p class="text-xs font-bold text-base-content/45">资源匹配</p>
                        <h2 class="mt-1 text-xl font-extrabold text-base-content">标签候选列表</h2>
                    </div>
                    <button class="btn btn-sm btn-circle btn-ghost" type="button" aria-label="关闭资源面板" @click="isShowResourceId3 = false">
                        <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                            <path
                                d="M18 6 6 18M6 6l12 12"
                                stroke="currentColor"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <resource :song-list="SongList" :section3-loading="section3Loading"></resource>
            </aside>
        </transition>

        <transition name="cloud-drawer-fade">
            <div v-if="showTranslation" class="fixed inset-0 z-40 bg-base-content/20" @click="showTranslation = false"></div>
        </transition>

        <transition name="cloud-drawer-slide">
            <aside
                v-if="showTranslation"
                class="fixed right-0 top-0 z-50 flex h-screen w-full flex-col overflow-y-auto border-l border-base-200 bg-base-100/95 p-4 shadow-2xl backdrop-blur md:w-[800px]"
            >
                <div class="flex items-center justify-between gap-3 border-b border-base-200/80 pb-4">
                    <div>
                        <p class="text-xs font-bold text-base-content/45">歌词翻译</p>
                        <h2 class="mt-1 text-xl font-extrabold text-base-content">翻译结果</h2>
                    </div>
                    <div class="flex items-center gap-2">
                        <button class="btn btn-sm btn-primary rounded-xl" type="button" @click="handleCopy('lyric_tran', translationText)">
                            应用到歌词
                        </button>
                        <button class="btn btn-sm btn-circle btn-ghost" type="button" aria-label="关闭歌词翻译" @click="showTranslation = false">
                            <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                                <path
                                    d="M18 6 6 18M6 6l12 12"
                                    stroke="currentColor"
                                    stroke-width="1.8"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <textarea
                    v-model="translationText"
                    class="mt-4 textarea textarea-bordered min-h-[calc(100vh-120px)] w-full rounded-2xl text-sm"
                ></textarea>
            </aside>
        </transition>

        <transition name="cloud-drawer-fade">
            <div v-if="showLyricsTimeline" class="fixed inset-0 z-40 bg-base-content/20" @click="showLyricsTimeline = false"></div>
        </transition>

        <transition name="cloud-drawer-slide">
            <aside
                v-if="showLyricsTimeline"
                class="fixed right-0 top-0 z-50 h-screen w-full overflow-y-auto border-l border-base-200 bg-base-100/95 p-4 shadow-2xl backdrop-blur"
            >
                <div class="mb-4 flex items-center justify-between gap-3">
                    <div>
                        <p class="text-xs font-bold text-base-content/45">歌词时间轴</p>
                        <h2 class="mt-1 text-xl font-extrabold text-base-content">时间轴编辑器</h2>
                    </div>
                    <button class="btn btn-sm btn-circle btn-ghost" type="button" aria-label="关闭时间轴编辑器" @click="showLyricsTimeline = false">
                        <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                            <path
                                d="M18 6 6 18M6 6l12 12"
                                stroke="currentColor"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <lyrics-timeline :play-path="playPath" :raw-lyrics="rawLyrics" @saveLyrics="handleSaveLyrics" />
            </aside>
        </transition>

        <transition name="cloud-drawer-fade">
            <div v-if="showVersionHistory" class="fixed inset-0 z-40 bg-base-content/20" @click="showVersionHistory = false"></div>
        </transition>

        <transition name="cloud-drawer-slide">
            <aside
                v-if="showVersionHistory"
                class="fixed right-0 top-0 z-50 h-screen w-full overflow-y-auto border-l border-base-200 bg-base-100/95 shadow-2xl backdrop-blur md:w-[900px]"
            >
                <div class="sticky top-0 z-10 border-b border-base-200/80 bg-base-100/95 px-6 py-4 backdrop-blur">
                    <div class="flex items-center justify-between gap-3">
                        <div>
                            <p class="text-xs font-bold text-base-content/45">版本历史</p>
                            <h2 class="mt-1 text-xl font-extrabold text-base-content">元数据版本记录</h2>
                        </div>
                        <button class="btn btn-sm btn-circle btn-ghost" type="button" aria-label="关闭版本历史" @click="showVersionHistory = false">
                            <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                                <path
                                    d="M18 6 6 18M6 6l12 12"
                                    stroke="currentColor"
                                    stroke-width="1.8"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                    <div v-if="versionTrackInfo && versionTrackInfo.title" class="mt-3 rounded-2xl bg-base-200/60 p-3">
                        <div class="font-semibold text-base-content">{{ versionTrackInfo.title }}</div>
                        <div class="mt-1 text-sm text-base-content/60">{{ versionTrackInfo.path }}</div>
                    </div>
                </div>

                <div class="p-6">
                    <MetadataVersionHistory ref="versionHistoryComponent" @restored="handleVersionRestored" />
                </div>
            </aside>
        </transition>

        <transition name="cloud-drawer-fade">
            <div
                v-if="isImagePreviewOpen"
                class="fixed inset-0 z-[60] bg-base-content/75 backdrop-blur-sm"
                @click="closeImagePreview"
            ></div>
        </transition>

        <transition name="cloud-drawer-slide">
            <div
                v-if="isImagePreviewOpen"
                class="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-6"
                @click.self="closeImagePreview"
            >
                <div class="relative max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[28px] bg-base-100 shadow-2xl">
                    <button
                        class="btn btn-sm btn-circle btn-ghost absolute right-3 top-3 z-10 bg-base-100/85 hover:bg-base-100"
                        type="button"
                        aria-label="关闭图片预览"
                        @click="closeImagePreview"
                    >
                        ✕
                    </button>

                    <div v-if="isImageLoading" class="flex min-h-[320px] items-center justify-center gap-3 p-8">
                        <span class="loading loading-spinner loading-lg text-primary"></span>
                        <span class="text-sm text-base-content/60">正在加载图片</span>
                    </div>

                    <div v-else class="flex max-h-[90vh] items-center justify-center bg-base-200/30 p-4 md:p-6">
                        <img
                            :src="previewImageUrl"
                            :alt="previewImageTitle || '图片预览'"
                            class="max-h-[calc(90vh-48px)] max-w-full object-contain"
                        />
                    </div>
                </div>
            </div>
        </transition>

        <transition name="cloud-drawer-fade">
            <div v-if="batchPanelOpen" class="fixed inset-0 z-30 bg-base-content/20" @click="closeBatchPanel"></div>
        </transition>

        <transition name="cloud-drawer-slide">
            <aside
                v-if="batchPanelOpen"
                class="cloud-side-drawer fixed right-0 top-0 z-40 h-screen w-full overflow-y-auto border-l border-base-200 bg-base-100 p-6 shadow-2xl md:w-[460px]"
            >
                <div class="flex items-start justify-between gap-3">
                    <div>
                        <p class="mb-1 text-xs font-bold text-base-content/45">批量操作</p>
                        <h2 class="text-2xl font-extrabold text-base-content">{{ batchPanelTitle }}</h2>
                        <p class="mt-2 text-sm text-base-content/55">
                            已选 {{ selectedCount }} 项，常用操作已前置，更多能力可在面板内按模块展开。
                        </p>
                    </div>
                    <button class="btn btn-sm btn-circle btn-ghost" type="button" aria-label="关闭批量面板" @click="closeBatchPanel">
                        <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                            <path
                                d="M18 6 6 18M6 6l12 12"
                                stroke="currentColor"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <div class="mt-5">
                    <home-cloud-batch-panel
                        :panel-key="batchPanelSection"
                        @change-panel="handleBatchPanelChange"
                    />
                </div>
            </aside>
        </transition>

        <transition name="cloud-batch-fade">
            <div
                v-if="selectedCount > 0 && moreActionsOpen"
                class="fixed bottom-[88px] left-1/2 z-30 max-h-[70vh] w-[min(760px,calc(100vw-24px))] -translate-x-1/2 overflow-y-auto rounded-[26px] border border-base-300/80 bg-base-100/95 p-3 shadow-2xl backdrop-blur"
            >
                <div class="flex items-center justify-between gap-3 px-2 pb-3 pt-1">
                    <div>
                        <p class="text-sm font-semibold text-base-content">更多操作</p>
                        <p class="mt-1 text-xs text-base-content/55">批量工具的子功能都集中在这里，按需直接打开。</p>
                    </div>
                    <button class="btn btn-sm btn-circle btn-ghost" type="button" aria-label="关闭更多操作" @click="toggleMoreActions(false)">
                        <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                            <path
                                d="M18 6 6 18M6 6l12 12"
                                stroke="currentColor"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <div class="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                    <button
                        v-for="action in visibleMoreActionItems"
                        :key="action.key"
                        class="rounded-2xl border border-base-300/80 bg-base-100 px-4 py-4 text-left transition hover:border-primary/30 hover:bg-primary/5"
                        type="button"
                        @click="openBatchAction(action.key)"
                    >
                        <div class="flex items-start gap-3">
                            <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-base-200/70 text-base-content">
                                <svg-icon v-if="action.iconName" :name="action.iconName" class="h-5 w-5 fill-current" />
                                <svg v-else-if="action.iconKey === 'split-metadata'" viewBox="0 0 14 16" fill="none" class="h-5 w-5">
                                    <path d="M2.5 0.25C1.25736 0.25 0.25 1.25736 0.25 2.5V3H1.75V2.5C1.75 2.08579 2.08579 1.75 2.5 1.75H11.5C11.9142 1.75 12.25 2.08579 12.25 2.5V3H13.75V2.5C13.75 1.25736 12.7426 0.25 11.5 0.25H2.5Z" fill="currentColor" />
                                    <path d="M1.75 13.5V7.5H0.25V13.5C0.25 14.7426 1.25736 15.75 2.5 15.75H11.5C12.7426 15.75 13.75 14.7426 13.75 13.5V7.5H12.25V13.5C12.25 13.9142 11.9142 14.25 11.5 14.25H2.5C2.08579 14.25 1.75 13.9142 1.75 13.5Z" fill="currentColor" />
                                    <path d="M2.5 4.5H0.25V6H2.5V4.5Z" fill="currentColor" />
                                    <path d="M6.51786 4.5H3.46429V6H6.51786V4.5Z" fill="currentColor" />
                                    <path d="M10.5357 4.5H7.48214V6H10.5357V4.5Z" fill="currentColor" />
                                    <path d="M13.75 4.5H11.5V6H13.75V4.5Z" fill="currentColor" />
                                </svg>
                            </div>
                            <div class="min-w-0">
                                <p class="text-sm font-semibold text-base-content">{{ action.label }}</p>
                                <p class="mt-1 text-xs leading-5 text-base-content/55">{{ action.description }}</p>
                            </div>
                        </div>
                    </button>
                </div>
            </div>

            <div
                v-if="selectedCount > 0"
                class="fixed bottom-5 left-1/2 z-30 flex max-w-[calc(100vw-24px)] -translate-x-1/2 items-center gap-3 rounded-2xl border border-base-300/80 bg-base-100 px-3 py-2.5 shadow-xl"
            >
                <div class="flex items-center gap-2 whitespace-nowrap rounded-xl bg-primary/10 px-2.5 py-2 text-primary">
                    <span class="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-primary text-xs font-extrabold text-primary-content">
                        {{ selectedCount }}
                    </span>
                    <span class="text-sm font-semibold">已选</span>
                </div>

                <div class="hidden h-6 w-px bg-base-300 md:block"></div>

                <div class="cloud-quick-actions flex items-center gap-2 overflow-x-auto pr-1">
                    <div
                        v-for="action in quickActionItems"
                        :key="action.key"
                        class="tooltip tooltip-top shrink-0"
                        :data-tip="action.label"
                    >
                        <button
                            class="btn btn-sm h-10 min-h-10 w-10 min-w-10 rounded-xl border shadow-none"
                            :class="action.key === 'more-actions'
                                ? 'btn-primary border-primary text-primary-content'
                                : 'border-base-300 bg-base-100 text-base-content hover:border-primary/30 hover:bg-primary/5'"
                            type="button"
                            :title="action.label"
                            :aria-label="action.label"
                            @click="handleQuickActionClick(action.key)"
                        >
                            <svg-icon v-if="action.iconName" :name="action.iconName" class="h-5 w-5 fill-current" />
                            <svg v-else-if="action.iconKey === 'manual-edit'" viewBox="0 0 15 15" fill="none" class="h-5 w-5">
                                <path d="M12.9697 0.969727L4.96973 8.96973L6.03039 10.0304L14.0304 2.03039L12.9697 0.969727Z" fill="currentColor" />
                                <path d="M2.5 1.25006C1.25736 1.25006 0.25 2.25742 0.25 3.50006V12.5001C0.25 13.7427 1.25736 14.7501 2.5 14.7501H11.5C12.7426 14.7501 13.75 13.7427 13.75 12.5001V7.00006H12.25V12.5001C12.25 12.9143 11.9142 13.2501 11.5 13.2501H2.5C2.08579 13.2501 1.75 12.9143 1.75 12.5001V3.50006C1.75 3.08585 2.08579 2.75006 2.5 2.75006H8V1.25006H2.5Z" fill="currentColor" />
                            </svg>
                            <svg v-else-if="action.iconKey === 'auto-scrape'" viewBox="0 0 1036 1024" fill="none" class="h-5 w-5">
                                <path d="M938.74776 69.162438L893.455144 52.837457a67.26383 67.26383 0 1 0-20.007307 64.317969l42.469499 15.465771c28.722146 10.433258 53.516478 49.097686 53.516478 83.466066v598.869028c0 34.000148-24.548843 72.664576-53.639223 82.729601l-38.296195 13.501863a67.26383 67.26383 0 1 0 14.115585 66.650109l46.397313-16.447724c55.60313-19.51633 98.195372-83.711555 98.195373-146.311105V216.210008c0-62.354061-42.960475-126.917519-98.195373-147.293059zM222.16703 888.971053a66.895597 66.895597 0 0 0-52.166292 24.548843L121.516773 897.440403A89.1123 89.1123 0 0 1 67.26383 822.443688V208.722611a89.97151 89.97151 0 0 1 54.252943-76.101414l51.920803-18.657121a67.632063 67.632063 0 1 0-15.465771-65.913643L98.195372 69.285182A157.358084 157.358084 0 0 0 0 208.722611v613.721077a156.253386 156.253386 0 0 0 99.30007 138.332731L159.56748 982.011168a67.509318 67.509318 0 1 0 62.59955-93.040115z" fill="currentColor" />
                                <path d="M426.29066 572.413721l86.780161-205.105584 91.935417 205.105584z m282.311696 230.759125h119.798354l-267.58239-592.854561-98.195372 1.104698-251.625642 592.118096 118.079936-1.350187L380.507068 680.183142h272.492158l55.112153 122.744215z" fill="currentColor" />
                            </svg>
                            <svg v-else-if="action.iconKey === 'tidy-folder'" viewBox="0 0 1024 1024" fill="none" class="h-5 w-5">
                                <path d="M512 133.034667L180.736 298.666667 512 464.298667 843.264 298.666667 512 133.034667z m19.072-85.845334l426.666667 213.333334a42.666667 42.666667 0 0 1 0 76.330666l-426.666667 213.333334a42.581333 42.581333 0 0 1-38.144 0l-426.666667-213.333334a42.666667 42.666667 0 0 1 0-76.330666l426.666667-213.333334a42.581333 42.581333 0 0 1 38.144 0z m388.522667 426.666667a42.666667 42.666667 0 0 1 38.186666 76.330667l-426.666666 213.333333a42.581333 42.581333 0 0 1-38.144 0l-426.666667-213.333333a42.666667 42.666667 0 0 1 38.186667-76.330667L512 677.632l407.594667-203.776z m0 213.333333a42.666667 42.666667 0 0 1 38.186666 76.330667l-426.666666 213.333333a42.581333 42.581333 0 0 1-38.144 0l-426.666667-213.333333a42.666667 42.666667 0 0 1 38.186667-76.330667L512 890.965333l407.594667-203.776z" fill="currentColor" />
                            </svg>
                            <svg v-else-if="action.iconKey === 'delete-folder'" viewBox="0 0 14 14" fill="none" class="h-5 w-5">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M9 0H5V1.5L9 1.5V0ZM0 2.5H14V4H12.75V11.75C12.75 12.9927 11.7426 14 10.5 14L3.65821 14C2.41556 14 1.4082 12.9927 1.4082 11.75V4H0V2.5ZM2.9082 4H11.25V11.75C11.25 12.1642 10.9142 12.5 10.5 12.5L3.6582 12.5C3.24399 12.5 2.9082 12.1642 2.9082 11.75V4ZM6.25 5.75V10.75H4.75V5.75H6.25ZM9.25 10.75V5.75H7.75V10.75H9.25Z" fill="currentColor" />
                            </svg>
                            <svg v-else-if="action.iconKey === 'format-music'" viewBox="0 0 1024 1024" fill="none" class="h-5 w-5">
                                <path d="M136.192 986.112C66.56 986.112 10.24 927.744 10.24 856.576v-141.312c0-71.68 56.32-130.048 125.952-130.048h241.664c69.632 0 125.952 58.368 125.952 130.048v141.312c0 71.68-56.32 130.048-125.952 130.048H136.192z m0-325.12c-28.672 0-52.224 24.064-52.224 54.272v141.312c0 29.696 23.552 54.272 52.224 54.272h241.152c28.672 0 52.224-24.064 52.224-54.272v-141.312c0-29.696-23.552-54.272-52.224-54.272H136.192z m485.888 308.736c-19.968 0-31.232-15.872-31.744-31.232-0.512-14.848 8.192-32.768 35.84-35.328 26.112-4.608 46.08-8.704 70.144-16.896l1.024-0.512h1.024c9.216 0 40.448-22.528 66.56-53.76l0.512-1.024 1.536-1.024h0.512c14.336-7.168 38.912-48.64 51.712-96.256 0.512-3.072 1.024-5.632 1.536-8.192l0.512-2.56c3.072-16.896 6.144-31.744 6.144-49.152 0-2.048 0-5.12-0.512-10.24-0.512-3.072-0.512-5.632-0.512-8.704l-37.376 45.568c-9.728 10.752-18.944 12.8-25.088 12.8-4.096 0-8.704-1.024-12.8-3.072-9.728-5.12-15.36-11.776-17.408-20.48-2.56-11.264 3.584-21.504 6.144-25.088l0.512-0.512 84.992-105.472c3.072-3.072 20.48-19.456 38.912-19.456 26.112 0 40.448 23.04 44.032 29.184l76.288 110.08s12.288 17.408-9.216 39.936c-7.168 7.168-15.36 11.264-23.04 11.264-9.728 0-15.872-5.632-16.896-6.656l-42.496-56.832v16.896c0 60.928-20.48 119.808-60.928 175.104-40.448 50.688-86.528 83.456-140.8 101.376-2.048 1.024-5.12 2.048-13.312 4.096l-4.096 1.024c-12.288 3.072-23.552 5.12-33.28 6.656h-18.432z m-456.704-451.584c-28.672 0-43.008-26.624-44.544-30.208l-71.68-113.152s-12.288-17.408 9.216-39.424c7.68-7.168 15.36-11.264 23.04-11.264 9.728 0 15.872 5.632 16.896 6.656l42.496 56.32v-1.536-6.144c0.512-25.6 3.584-51.2 8.704-75.776 11.776-48.64 38.912-94.208 82.432-139.776 41.984-38.912 90.624-64 139.776-72.192 17.408-4.608 34.816-4.608 52.224-4.608 9.728 0 15.36 13.312 15.872 26.112 1.024 17.92-7.68 37.888-35.84 40.96l-2.048 0.512c-19.968 4.096-40.448 8.192-59.904 16.384-38.4 15.872-67.584 39.936-94.72 76.8l-4.608 6.656-2.048-1.536c-6.144 8.704-20.992 35.84-28.672 71.168l-0.512 3.072-0.512 1.536v0.512c-0.512 2.048-5.12 25.088-5.12 51.2v4.096c0 3.072 0.512 6.656 0.512 9.728 0 1.536 0 2.56 0.512 4.096l33.792-40.96c9.728-10.752 18.944-12.8 25.088-12.8 4.608 0 8.704 1.024 12.8 3.072 9.728 5.12 15.36 11.776 17.408 19.968 1.536 7.68-0.512 16.896-6.144 25.088v0.512L204.8 498.176c-3.584 3.584-20.992 19.968-39.424 19.968z m471.04-42.496c-69.632 0-125.952-58.368-125.952-130.048V204.8c0-71.68 56.32-130.048 125.952-130.048h241.152C947.2 74.752 1003.52 133.12 1003.52 204.8v141.312c0 71.68-56.32 130.048-125.952 130.048h-241.152z m0-325.12c-28.672 0-52.224 24.064-52.224 54.272v141.312c0 29.696 23.552 54.272 52.224 54.272h241.152c28.672 0 52.224-24.064 52.224-54.272V204.8c0-29.696-23.552-54.272-52.224-54.272h-241.152z" fill="currentColor" />
                            </svg>
                            <svg v-else-if="action.iconKey === 'split-cue'" viewBox="0 0 16 14" fill="none" class="h-5 w-5">
                                <path d="M6.00001 0.750244C6.00001 0.33603 5.66422 0.000244141 5.25001 0.000244141C4.83579 0.000244141 4.50001 0.336031 4.50001 0.750244L4.50001 1.10975C4.50001 1.72344 4.75068 2.31051 5.19395 2.73492L7.26701 4.71982L4.82988 7.09988C4.42039 7.49039 4.31633 8.10894 4.59285 8.61168L4.60292 8.63001L6.88601 12.2829C6.96051 12.4021 7.00001 12.5399 7.00001 12.6804V13.2502C7.00001 13.6645 7.33579 14.0002 7.75001 14.0002C8.16422 14.0002 8.50001 13.6645 8.50001 13.2502L8.50001 12.6804C8.50001 12.2587 8.3815 11.8455 8.158 11.4879L6.00724 8.04672L8.47876 5.63307C8.8733 5.28191 8.97223 4.73973 8.81074 4.29147C8.7162 4.02904 8.53253 3.85435 8.46556 3.79066L8.45717 3.78266L6.23132 1.65147C6.08357 1.51 6.00001 1.31431 6.00001 1.10975L6.00001 0.750244Z" fill="currentColor" />
                                <path d="M2.5 1.75024C2.08421 1.75024 1.75 2.08446 1.75 2.50024V11.5002C1.75 11.916 2.08421 12.2502 2.5 12.2502H5.25C5.66421 12.2502 6 12.586 6 13.0002C6 13.4145 5.66421 13.7502 5.25 13.7502H2.5C1.25579 13.7502 0.25 12.7445 0.25 11.5002V2.50024C0.25 1.25603 1.25579 0.250244 2.5 0.250244H3.11C3.52421 0.250244 3.86 0.586031 3.86 1.00024C3.86 1.41446 3.52421 1.75024 3.11 1.75024H2.5Z" fill="currentColor" />
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M8.47296 2.23137C7.87783 1.41306 8.4474 0.250244 9.47952 0.250244H13.4995C14.7437 0.250244 15.7495 1.25603 15.7495 2.50024V11.5002C15.7495 12.7445 14.7437 13.7502 13.4995 13.7502H11.1795C10.7359 13.7502 10.302 13.5087 10.0856 13.0893L7.90236 9.06168L7.90098 9.05917C7.63468 8.57234 7.71835 7.95284 8.13705 7.5617L10.6534 4.96992L8.48264 2.24467L8.47296 2.23137ZM10.0065 1.75024L11.9884 4.23833L11.9998 4.25432C12.3627 4.7623 12.2997 5.45211 11.8588 5.88178L9.30861 8.50837L11.337 12.2502H13.4995C13.9153 12.2502 14.2495 11.916 14.2495 11.5002V2.50024C14.2495 2.08446 13.9153 1.75024 13.4995 1.75024H10.0065Z" fill="currentColor" />
                            </svg>
                            <svg v-else-if="action.iconKey === 'replace-text'" viewBox="0 0 12 16" fill="none" class="h-5 w-5">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M12.0001 4.25006V5.75006L0 5.75006V4.93951L3.96979 0.969727L5.03045 2.03039L2.81078 4.25006L12.0001 4.25006ZM0.000116289 11.7501L0.000116348 10.2501L12 10.2501V11.0608L8.03045 15.0304L6.96979 13.9697L9.18946 11.7501H0.000116289Z" fill="currentColor" />
                            </svg>
                            <svg v-else-if="action.iconKey === 'more-actions'" viewBox="0 0 24 24" fill="none" class="h-5 w-5">
                                <path d="M12 5.5a1.5 1.5 0 1 0 0 .01M12 12a1.5 1.5 0 1 0 0 .01M12 18.5a1.5 1.5 0 1 0 0 .01" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                <button
                    class="btn btn-sm btn-ghost btn-circle shrink-0 text-base-content/55 hover:text-base-content"
                    type="button"
                    aria-label="清空勾选"
                    @click="clearSelection"
                >
                    <svg viewBox="0 0 24 24" fill="none" class="h-4 w-4">
                        <path
                            d="M18 6 6 18M6 6l12 12"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>
        </transition>

        <div class="cloud-tag-editor-host">
            <batch-operation-runner
                ref="batchOperationRunner"
                :checked-data="checkedData"
                :file-path="filePath"
            />
            <batch-settings ref="batchSettings" />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import HomeCloudBatchPanel from './HomeCloudBatchPanel.vue'
import BatchSettings from '@/components/batch/BatchSettings.vue'
import BatchOperationRunner from '@/components/batch/BatchOperationRunner.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import MusicMetaData from './musicMetaData.vue'
import Resource from './resource.vue'
import LyricsTimeline from './lyricsTimeline.vue'
import MetadataVersionHistory from './MetadataVersionHistory.vue'
import { splitArtistText } from '@/utils/artistSplitter'

const HOME_CLOUD_LIST_FIELDS_STORAGE_KEY = 'showFieldsHomeCloudList'
const HOME_CLOUD_DEFAULT_LIST_FIELDS = ['title', 'artist', 'album', 'year', 'status']
const HOME_CLOUD_LIST_FIELD_OPTIONS = [
    { id: 'album_img', label: '专辑封面', width: '88px' },
    { id: 'title', label: '标题', width: 'minmax(200px,2fr)' },
    { id: 'filename', label: '文件名', width: 'minmax(180px,1.6fr)' },
    { id: 'artist', label: '艺术家', width: 'minmax(120px,1fr)' },
    { id: 'album', label: '专辑', width: 'minmax(120px,1fr)' },
    { id: 'album_type', label: '专辑类型', width: 'minmax(110px,0.9fr)' },
    { id: 'albumartist', label: '专辑艺术家', width: 'minmax(120px,1fr)' },
    { id: 'discnumber', label: '光盘编号', width: 'minmax(96px,0.8fr)' },
    { id: 'tracknumber', label: '音轨号', width: 'minmax(96px,0.8fr)' },
    { id: 'genre', label: '风格', width: 'minmax(110px,0.9fr)' },
    { id: 'year', label: '年份', width: 'minmax(90px,0.8fr)' },
    { id: 'lyrics', label: '歌词', width: 'minmax(90px,0.75fr)' },
    { id: 'comment', label: '简介', width: 'minmax(160px,1.2fr)' },
    { id: 'duration', label: '时长', width: 'minmax(90px,0.75fr)' },
    { id: 'size', label: '文件大小', width: 'minmax(100px,0.85fr)' },
    { id: 'bit_rate', label: '比特率', width: 'minmax(100px,0.85fr)' },
    { id: 'sample_rate', label: '采样率', width: 'minmax(100px,0.85fr)' },
    { id: 'channels', label: '声道', width: 'minmax(90px,0.75fr)' },
    { id: 'bit_depth', label: '位深度', width: 'minmax(90px,0.75fr)' },
    { id: 'language', label: '语言', width: 'minmax(90px,0.75fr)' },
    { id: 'status', label: '状态', width: 'minmax(110px,0.8fr)' },
    { id: 'type', label: '类型', width: 'minmax(100px,0.8fr)' },
    { id: 'updatedAt', label: '时间', width: 'minmax(140px,1fr)' },
]

export default {
    name: 'homeCloud',
    components: {
        HomeCloudBatchPanel,
        BatchSettings,
        BatchOperationRunner,
        SvgIcon,
        MusicMetaData,
        Resource,
        LyricsTimeline,
        MetadataVersionHistory
    },
    data() {
        const storedListFields = localStorage.getItem(HOME_CLOUD_LIST_FIELDS_STORAGE_KEY)
        let initialListFields = [...HOME_CLOUD_DEFAULT_LIST_FIELDS]

        if (storedListFields) {
            try {
                const parsedListFields = JSON.parse(storedListFields)
                if (Array.isArray(parsedListFields) && parsedListFields.length > 0) {
                    initialListFields = parsedListFields
                }
            } catch (error) {
                initialListFields = [...HOME_CLOUD_DEFAULT_LIST_FIELDS]
            }
        }

        return {
            viewMode: 'card',
            activeItemId: null,
            batchPanelOpen: false,
            batchPanelTitle: '批量编辑',
            batchPanelSection: 'tag-editor',
            moreActionsOpen: false,
            activeQuickActionKey: '',
            activeTab: '全部',
            localSearchWord: '',
            selectedFilterId: null,
            selectedArtistFilter: '',
            selectedAlbumFilter: '',
            selectedYearFilter: '',
            selectedArtworkFilter: '',
            isDragSelecting: false,
            dragStartId: null,
            dragLastId: null,
            dragSelectionMode: 'select',
            dragPreviewIds: [],
            dragInitialCheckedIds: [],
            dragInitialCheckedData: [],
            isLoading: false,
            loadError: '',
            detailLoading: false,
            detailError: '',
            fileEntries: [],
            listEntries: [],
            rootPath: '',
            fullPath: '',
            detailMusicInfo: {},
            detailTarget: null,
            reloadImg: true,
            translationText: '',
            SongList: [],
            section3Loading: false,
            isShowResourceId3: false,
            showTranslation: false,
            showLyricsTimeline: false,
            playPath: '',
            rawLyrics: '',
            showVersionHistory: false,
            versionTrackInfo: null,
            isImagePreviewOpen: false,
            isImageLoading: false,
            previewImageUrl: '',
            previewImageTitle: '',
            currentPage: 1,
            pageSize: parseInt(localStorage.getItem('pageSize'), 10) || 50,
            totalCount: 0,
            hasMoreData: false,
            isLoadingMore: false,
            nextCursor: null,
            lastRequestParams: null,
            loadMoreObserver: null,
            uniqueArtistOptions: [],
            uniqueAlbumOptions: [],
            uniqueYearOptions: [],
            uniqueGenreOptions: [],
            uniqueArtworkOptions: [],
            homeCloudListFieldOptions: HOME_CLOUD_LIST_FIELD_OPTIONS,
            visibleHomeCloudListFields: initialListFields,
            pageSizeOptions: [20, 50, 100, 200],
            tabs: ['全部', '音频', '图片'],
            filterOptions: [
                { id: 'state:null', label: '过滤: 未刮削' },
                { id: 'state:success', label: '过滤: 成功' },
                { id: 'state:failed', label: '过滤: 失败' }
            ],
            sortOptions: [
                { id: 'name', label: '名称' },
                // { id: 'title', label: '标题' },
                // { id: 'artist', label: '艺术家' },
                // { id: 'album', label: '专辑' },
                // { id: 'year', label: '年份' },
                { id: 'update_time', label: '修改时间' },
                { id: 'size', label: '大小' },
                { id: 'state', label: '状态' },
                { id: 'folder', label: '目录' }
            ],
            quickActionItems: [
                { key: 'tag-editor', label: '批量编辑', iconKey: 'manual-edit' },
                { key: 'auto-scrape', label: '自动刮削', iconKey: 'auto-scrape' },
                { key: 'background-scrape', label: '后台刮削', iconName: 'sys-backend' },
                { key: 'tidy-folder', label: '整理文件', iconKey: 'tidy-folder' },
                { key: 'delete-folder', label: '删除文件夹', iconKey: 'delete-folder' },
                { key: 'format-music', label: '格式转换', iconKey: 'format-music' },
                { key: 'split-cue', label: '分割音轨', iconKey: 'split-cue' },
                { key: 'replace-text', label: '替换文本', iconKey: 'replace-text' },
                { key: 'file-index', label: '文件索引', iconName: 'file-index' },
                { key: 'batch-settings', label: '更多设置', iconName: 'settings' },
                { key: 'more-actions', label: '更多操作', iconKey: 'more-actions' }
            ],
            moreActionItems: [
                {
                    key: 'background-scrape',
                    label: '后台刮削',
                    iconName: 'sys-backend',
                    description: '把已选文件提交到后台批量刮削任务。'
                },
                {
                    key: 'delete-folder',
                    label: '删除文件夹',
                    iconKey: 'delete-folder',
                    description: '删除空文件夹或无音乐文件夹。'
                },
                {
                    key: 'format-music',
                    label: '格式转换',
                    iconKey: 'format-music',
                    description: '批量转换音频格式、比特率和采样率。'
                },
                {
                    key: 'split-cue',
                    label: '分割音轨',
                    iconKey: 'split-cue',
                    description: '使用 CUE 文件切分整轨音频。'
                },
                {
                    key: 'import-music',
                    label: '导入收藏',
                    iconName: 'import-music',
                    description: '把本地音乐导入到音乐收藏库。'
                },
                {
                    key: 'duplicate-check',
                    label: '重复文件检查',
                    iconName: 'duplicate-file',
                    description: '检查并清理重复音乐文件。'
                },
                {
                    key: 'replay-gain',
                    label: '回放增益',
                    iconName: 'gain',
                    description: '为文件批量计算回放增益。'
                },
                {
                    key: 'generate-strm',
                    label: '生成strm文件',
                    iconName: 'cloud-down',
                    description: '批量生成 strm 流媒体索引文件。'
                },
                {
                    key: 'language-convert',
                    label: '语言转换',
                    iconName: 'transform',
                    description: '批量做简繁体转换。'
                },
                {
                    key: 'split-metadata',
                    label: '拆分元数据',
                    iconKey: 'split-metadata',
                    description: '从文件名中拆分标题、艺术家等字段。'
                },
                {
                    key: 'replace-text',
                    label: '替换文本',
                    iconKey: 'replace-text',
                    description: '按规则批量替换标签文本。'
                },
                {
                    key: 'encoding-fix',
                    label: '乱码修复',
                    iconName: 'fix-encoding',
                    description: '批量修复编码异常的元数据文本。'
                },
                {
                    key: 'file-index',
                    label: '文件索引',
                    iconName: 'file-index',
                    description: '增量或重建当前目录的音乐索引。'
                },
                {
                    key: 'batch-settings',
                    label: '更多设置',
                    iconName: 'settings',
                    description: '调整详情、遍历和显示字段等公共设置。'
                }
            ]
        }
    },
    computed: {
        ...mapGetters(['geFullPath', 'sortedField', 'searchWord', 'getUserRole']),
        filePath: {
            get() {
                return this.geFullPath
            },
            set(value) {
                this.$store.commit('setFullPath', value)
            }
        },
        userRole() {
            return this.getUserRole
        },
        checkedIds: {
            get() {
                return this.$store.state.common.checkedIds
            },
            set(value) {
                this.$store.commit('setCheckedIds', value)
            }
        },
        checkedData: {
            get() {
                return this.$store.state.common.checkedData
            },
            set(value) {
                this.$store.commit('setCheckedData', value)
            }
        },
        breadcrumbs() {
            if (!this.filePath) {
                return []
            }

            if (!this.rootPath || !this.filePath.startsWith(this.rootPath)) {
                const parts = this.filePath.split('/').filter(Boolean)
                let currentPath = ''
                return parts.map((part) => {
                    currentPath += `/${part}`
                    return {
                        name: part,
                        path: currentPath
                    }
                })
            }

            const segments = [{
                name: this.$tt ? this.$tt('根目录') : '根目录',
                path: this.rootPath
            }]

            const rest = this.filePath.replace(this.rootPath, '')
            const parts = rest.split('/').filter(Boolean)
            let currentPath = this.rootPath
            parts.forEach((part) => {
                currentPath = currentPath.endsWith('/') ? `${currentPath}${part}` : `${currentPath}/${part}`
                segments.push({
                    name: part,
                    path: currentPath
                })
            })
            return segments
        },
        visibleMoreActionItems() {
            const quickActionKeys = new Set(
                (this.quickActionItems || [])
                    .map((item) => item.key)
                    .filter((key) => key && key !== 'more-actions')
            )

            return (this.moreActionItems || []).filter((item) => !quickActionKeys.has(item.key))
        },
        currentFolderName() {
            if (!this.filePath) {
                return '-'
            }
            const parts = this.filePath.split('/').filter(Boolean)
            return parts[parts.length - 1] || this.filePath
        },
        visibleEntries() {
            const source = this.fileEntries
            if (this.activeTab === '全部') {
                return source
            }
            if (this.activeTab === '音频') {
                return source.filter((item) => item.kind === 'audio')
            }
            if (this.activeTab === '图片') {
                return source.filter((item) => item.kind === 'image')
            }
            return source
        },
        visibleListEntries() {
            const source = this.listEntries
            if (this.activeTab === '全部') {
                return source
            }
            if (this.activeTab === '音频') {
                return source.filter((item) => item.kind === 'audio')
            }
            if (this.activeTab === '图片') {
                return source.filter((item) => item.kind === 'image')
            }
            return source
        },
        visibleListColumns() {
            const allowedFieldIds = new Set((this.homeCloudListFieldOptions || []).map((item) => item.id))
            const selectedFieldIds = Array.isArray(this.visibleHomeCloudListFields)
                ? this.visibleHomeCloudListFields.filter((fieldId) => allowedFieldIds.has(fieldId))
                : []
            const normalizedFieldIds = selectedFieldIds.length > 0
                ? selectedFieldIds
                : HOME_CLOUD_DEFAULT_LIST_FIELDS

            return (this.homeCloudListFieldOptions || []).filter((item) => normalizedFieldIds.includes(item.id))
        },
        listGridTemplateColumns() {
            const columns = this.visibleListColumns.map((item) => item.width)
            return ['48px', ...columns, '90px'].join(' ')
        },
        currentViewEntries() {
            return this.viewMode === 'card' ? this.visibleEntries : this.visibleListEntries
        },
        selectedCount() {
            return this.checkedIds.length
        },
        activeItem() {
            return [...this.fileEntries, ...this.listEntries].find((item) => item.id === this.activeItemId) || this.detailTarget || null
        },
        selectedFilterOption() {
            return this.filterOptions.find((item) => item.id === this.selectedFilterId) || null
        },
        activeMetaFilterChips() {
            if (this.viewMode !== 'list') {
                return []
            }

            const chips = []

            if (this.selectedArtistFilter) {
                chips.push({
                    key: 'artist',
                    label: `艺术家：${this.getOptionLabel(this.uniqueArtistOptions, this.selectedArtistFilter)}`
                })
            }
            if (this.selectedAlbumFilter) {
                chips.push({
                    key: 'album',
                    label: `专辑：${this.getOptionLabel(this.uniqueAlbumOptions, this.selectedAlbumFilter)}`
                })
            }
            if (this.selectedYearFilter) {
                chips.push({
                    key: 'year',
                    label: `年份：${this.getOptionLabel(this.uniqueYearOptions, this.selectedYearFilter)}`
                })
            }
            if (this.selectedArtworkFilter) {
                chips.push({
                    key: 'artwork',
                    label: this.selectedArtworkFilter === 'has' ? '封面：有封面' : '封面：无封面'
                })
            }

            return chips
        },
        sortSummary() {
            if (!Array.isArray(this.sortedField) || this.sortedField.length === 0) {
                return ''
            }

            return this.sortedField.map((field) => {
                const direction = field.startsWith('-') ? '降序' : '升序'
                const fieldId = field.startsWith('-') ? field.slice(1) : field
                const option = this.sortOptions.find((item) => item.id === fieldId)
                const label = option ? option.label : fieldId
                return `${label}${direction}`
            }).join(' / ')
        },
        dragStatusLabel() {
            if (!this.isDragSelecting || this.dragPreviewIds.length === 0) {
                return ''
            }

            return this.dragSelectionMode === 'deselect'
                ? `取消中 ${this.dragPreviewIds.length} 项`
                : `选择中 ${this.dragPreviewIds.length} 项`
        },
        allSelected() {
            return this.currentViewEntries.length > 0 && this.currentViewEntries.every((item) => this.checkedIds.includes(item.id))
        }
    },
    watch: {
        filePath: {
            handler(newPath, oldPath) {
                if (!newPath || newPath === oldPath) {
                    return
                }
                if (!this.rootPath || !newPath.startsWith(this.rootPath)) {
                    this.syncRootPath(newPath)
                }
                this.fetchCurrentView()
            }
        }
    },
    created() {
        this.initSearchState()
        this.syncRootPath(this.filePath)
        this.fetchCurrentView()
    },
    mounted() {
        window.addEventListener('pointerup', this.finishDragSelection)
        window.addEventListener('pointercancel', this.finishDragSelection)
        window.addEventListener('keydown', this.handleGlobalKeydown)
        this.initLoadMoreObserver()
    },
    beforeDestroy() {
        window.removeEventListener('pointerup', this.finishDragSelection)
        window.removeEventListener('pointercancel', this.finishDragSelection)
        window.removeEventListener('keydown', this.handleGlobalKeydown)
        this.disconnectLoadMoreObserver()
    },
    methods: {
        badgeClass(tone) {
            const map = {
                success: 'badge-success text-success-content',
                warning: 'badge-warning text-warning-content',
                neutral: 'badge-ghost',
                primary: 'badge-primary text-primary-content',
                danger: 'badge-error text-error-content'
            }
            return map[tone] || 'badge-ghost'
        },
        iconToneClass(item) {
            if (!item) {
                return 'text-base-content/65'
            }
            if (item.kind === 'folder') {
                return 'text-info'
            }
            if (item.kind === 'image') {
                return 'text-success'
            }
            if (item.kind === 'audio') {
                return this.isPlayableAudioItem(item) ? 'text-primary' : 'text-warning'
            }
            if (item.kind === 'document') {
                return 'text-secondary'
            }
            return 'text-base-content/65'
        },
        resetTransientPanels() {
            this.isShowResourceId3 = false
            this.showTranslation = false
            this.showLyricsTimeline = false
            this.showVersionHistory = false
            this.closeAllDirectBatchActions()
            this.activeQuickActionKey = ''
        },
        resetDragSelectionState() {
            this.isDragSelecting = false
            this.dragStartId = null
            this.dragLastId = null
            this.dragSelectionMode = 'select'
            this.dragPreviewIds = []
            this.dragInitialCheckedIds = []
            this.dragInitialCheckedData = []
        },
        resetFetchState(options = {}) {
            const { preserveContext = false, preserveSelection = false } = options

            this.loadError = ''
            this.resetDragSelectionState()

            if (!preserveContext) {
                this.activeItemId = null
                this.batchPanelOpen = false
                this.moreActionsOpen = false
                this.detailMusicInfo = {}
                this.detailTarget = null
                this.detailError = ''
                this.resetTransientPanels()
            }

            if (!preserveSelection) {
                this.checkedIds = []
                this.checkedData = []
            }
        },
        async fetchFileList(refresh = false, options = {}) {
            if (!this.filePath) {
                return
            }

            this.isLoading = true
            this.resetFetchState(options)
            this.listEntries = []
            this.hasMoreData = false
            this.totalCount = 0
            this.lastRequestParams = null
            this.clearUniqueOptions()

            try {
                const res = await this.$api.Task.fileList({
                    file_path: this.filePath,
                    sorted_fields: this.sortedField,
                    search_word: this.searchWord,
                    refresh
                })

                if (!res.result) {
                    throw new Error(res.message || '加载目录失败')
                }

                this.fileEntries = this.normalizeEntries(res.data.file_list_data || [])
                this.totalCount = this.fileEntries.length
            } catch (error) {
                this.loadError = error.message || '加载目录失败'
            } finally {
                this.isLoading = false
            }
        },
        buildDirectorySortPayload() {
            const sortFieldMap = {
                name: 'name',
                title: 'title',
                artist: 'artist',
                album: 'album',
                year: 'year',
                update_time: 'update_time',
                size: 'size',
                state: 'state',
                folder: 'kind'
            }

            return (Array.isArray(this.sortedField) ? this.sortedField : [])
                .map((field) => {
                    const order = field.startsWith('-') ? 'desc' : 'asc'
                    const fieldId = field.startsWith('-') ? field.slice(1) : field
                    const mappedField = sortFieldMap[fieldId]
                    if (!mappedField) {
                        return null
                    }
                    return {
                        field: mappedField,
                        order
                    }
                })
                .filter(Boolean)
        },
        buildDirectoryQueryParams() {
            const states = []
            if (this.selectedFilterId && this.selectedFilterId.startsWith('state:')) {
                states.push(this.selectedFilterId.split(':')[1])
            }

            return {
                path: this.filePath,
                recursive: false,
                keyword: this.localSearchWord.trim(),
                states,
                artists: this.selectedArtistFilter ? [this.selectedArtistFilter] : [],
                albums: this.selectedAlbumFilter ? [this.selectedAlbumFilter] : [],
                years: this.selectedYearFilter ? [Number(this.selectedYearFilter)] : [],
                has_artwork: this.selectedArtworkFilter === 'has'
                    ? true
                    : (this.selectedArtworkFilter === 'none' ? false : null),
                sort: this.buildDirectorySortPayload(),
                limit: this.pageSize
            }
        },
        buildId3RequestParams(refresh = false) {
            const fileName = this.getBaseName(this.filePath)
            const directoryName = this.getDirectoryName(this.filePath)
            let depth = 1

            if (localStorage.getItem('isDepth') === 'true') {
                depth = -1
            }

            return {
                file_full_path: directoryName,
                select_data: [{
                    icon: 'icon-folder',
                    name: fileName,
                    title: fileName,
                }],
                mode: 'all',
                limit: localStorage.getItem('limit') || 100,
                depth,
                sorted_fields: this.sortedField,
                search_word: this.searchWord,
                refresh
            }
        },
        clearUniqueOptions() {
            this.uniqueArtistOptions = []
            this.uniqueAlbumOptions = []
            this.uniqueYearOptions = []
            this.uniqueGenreOptions = []
            this.uniqueArtworkOptions = []
        },
        mergeOptionList(currentList, nextList, keyGetter) {
            const result = new Map()
            currentList.forEach((item) => {
                result.set(keyGetter(item), item)
            })
            nextList.forEach((item) => {
                result.set(keyGetter(item), item)
            })
            return [...result.values()]
        },
        updateUniqueOptions(list, replace = false) {
            const artistOptions = []
            const albumOptions = []
            const yearOptions = []
            const genreOptions = []
            const artworkOptions = []
            const sysDict = this.$store.state.common.sysDict || {}

            list.forEach((item) => {
                const artistValues = splitArtistText(item.artist || '', sysDict)
                const normalizedArtists = artistValues.length > 0
                    ? artistValues
                    : (item.artist ? [item.artist] : [])

                normalizedArtists.forEach((artist) => {
                    if (artist) {
                        artistOptions.push({ id: artist, name: artist })
                    }
                })

                if (item.album) {
                    albumOptions.push({ id: item.album, name: item.album })
                }

                if (item.year) {
                    const year = String(item.year)
                    yearOptions.push({ id: year, name: year })
                }

                if (item.genre) {
                    String(item.genre).split(/[\/,;]/).map(value => value.trim()).filter(Boolean).forEach((genre) => {
                        genreOptions.push({ id: genre, name: genre })
                    })
                }

                if (item.artwork) {
                    artworkOptions.push({
                        url: item.artwork,
                        album: item.album || '',
                        artist: item.artist || ''
                    })
                }
            })

            if (replace) {
                this.uniqueArtistOptions = this.mergeOptionList([], artistOptions, item => item.id)
                this.uniqueAlbumOptions = this.mergeOptionList([], albumOptions, item => item.id)
                this.uniqueYearOptions = this.mergeOptionList([], yearOptions, item => item.id)
                this.uniqueGenreOptions = this.mergeOptionList([], genreOptions, item => item.id)
                this.uniqueArtworkOptions = this.mergeOptionList([], artworkOptions, item => item.url)
                return
            }

            this.uniqueArtistOptions = this.mergeOptionList(this.uniqueArtistOptions, artistOptions, item => item.id)
            this.uniqueAlbumOptions = this.mergeOptionList(this.uniqueAlbumOptions, albumOptions, item => item.id)
            this.uniqueYearOptions = this.mergeOptionList(this.uniqueYearOptions, yearOptions, item => item.id)
            this.uniqueGenreOptions = this.mergeOptionList(this.uniqueGenreOptions, genreOptions, item => item.id)
            this.uniqueArtworkOptions = this.mergeOptionList(this.uniqueArtworkOptions, artworkOptions, item => item.url)
        },
        normalizeDirectoryEntries(list) {
            return (Array.isArray(list) ? list : []).map((item, index) => {
                const kind = item.kind || 'file'
                const relativePath = item.relative_dir
                    ? `${item.relative_dir}/${item.name}`
                    : item.name
                const subtitle = kind === 'folder'
                    ? '文件夹'
                    : (item.artist || item.relative_dir || '-')

                return {
                    id: item.id || `folder-${item.folder_id || index}`,
                    kind,
                    trackId: item.track_id || null,
                    title: item.title || item.name || '未命名',
                    fileName: relativePath || item.title || '未命名',
                    fullPath: item.full_path || this.filePath,
                    relativeDir: item.relative_dir || '',
                    updatedAt: item.year ? `${item.year}` : '-',
                    sizeLabel: subtitle,
                    status: item.status || '普通文件',
                    statusTone: item.status_tone || 'neutral',
                    typeLabel: item.file_type || '-',
                    artist: item.artist || '-',
                    album: item.album || '-',
                    year: item.year || '-',
                    artwork: item.artwork || '',
                    raw: {
                        ...item,
                        id: item.id || `folder-${item.folder_id || index}`,
                        track_id: item.track_id || null,
                        icon: kind === 'folder' ? 'icon-folder' : (kind === 'image' ? 'icon-image' : item.icon || ''),
                        filename: item.name || '',
                        full_path: item.full_path || this.filePath,
                        parent_path: item.parent_path || this.filePath,
                    }
                }
            })
        },
        async fetchDirectoryList(options = {}) {
            if (!this.filePath) {
                return
            }

            this.isLoading = true
            this.resetFetchState(options)
            this.currentPage = 1
            this.hasMoreData = false
            this.nextCursor = null
            this.totalCount = 0
            this.lastRequestParams = this.buildDirectoryQueryParams()
            this.clearUniqueOptions()

            try {
                const res = await this.$api.Task.directoryQuery(this.lastRequestParams)

                if (!res.result) {
                    throw new Error(res.message || '加载目录列表失败')
                }

                const responseData = res.data || {}
                const list = Array.isArray(responseData.items) ? responseData.items : []
                const normalizedEntries = this.normalizeDirectoryEntries(list)
                this.fileEntries = normalizedEntries
                this.listEntries = normalizedEntries
                this.totalCount = responseData.total || this.listEntries.length
                this.hasMoreData = Boolean(responseData.has_more)
                this.nextCursor = responseData.next_cursor || null
                this.updateUniqueOptions(list, true)
                this.$nextTick(() => {
                    this.observeLoadMoreSentinel()
                })
            } catch (error) {
                this.loadError = error.message || '加载目录列表失败'
            } finally {
                this.isLoading = false
            }
        },
        async fetchFileId3List(refresh = false, options = {}) {
            if (!this.filePath) {
                return
            }

            this.isLoading = true
            this.resetFetchState(options)
            this.currentPage = 1
            this.hasMoreData = false
            this.totalCount = 0
            this.lastRequestParams = this.buildId3RequestParams(refresh)
            this.clearUniqueOptions()

            try {
                const res = await this.$api.Task.fileID3List({
                    ...this.lastRequestParams,
                    page: 1,
                    page_size: this.pageSize
                })

                if (!res.result) {
                    throw new Error(res.message || '加载元数据列表失败')
                }

                const responseData = res.data
                const list = Array.isArray(responseData?.list)
                    ? responseData.list
                    : (Array.isArray(res.data) ? res.data : [])
                this.listEntries = this.normalizeId3Entries(list)
                this.totalCount = responseData?.total || this.listEntries.length
                this.hasMoreData = Boolean(responseData?.has_more)
                this.updateUniqueOptions(list, true)
            } catch (error) {
                this.loadError = error.message || '加载元数据列表失败'
            } finally {
                this.isLoading = false
            }
        },
        fetchCurrentView(refresh = false, options = {}) {
            return this.fetchDirectoryList(options)
        },
        refreshCurrentView(options = {}) {
            this.fetchCurrentView(true, options)
        },
        openTopFileIndexer() {
            const runner = this.$refs.batchOperationRunner
            if (runner && typeof runner.openOperationDrawer === 'function') {
                runner.openOperationDrawer('file_index')
            }
        },
        switchViewMode(mode) {
            if (this.viewMode === mode) {
                return
            }
            this.viewMode = mode
            this.fetchCurrentView()
        },
        normalizeEntries(treeData) {
            let nodes = Array.isArray(treeData) ? treeData : []

            if (nodes.length === 1 && Array.isArray(nodes[0].children)) {
                nodes = nodes[0].children
            }

            return nodes.map((node, index) => {
                const fullPath = node.full_path || (this.filePath.endsWith('/')
                    ? `${this.filePath}${node.name}`
                    : `${this.filePath}/${node.name}`)

                const kind = this.resolveKind(node)
                return {
                    id: node.id || `${kind}-${fullPath}-${index}`,
                    kind,
                    title: node.title || node.name || '未命名',
                    fileName: node.name || node.title || '未命名',
                    fullPath,
                    updatedAt: node.update_time || node.updated_at || node.modify_time || '-',
                    sizeLabel: this.formatSizeLabel(node, kind),
                    status: this.resolveStatus(node, kind),
                    statusTone: this.resolveStatusTone(node, kind),
                    typeLabel: this.resolveTypeLabel(node, kind),
                    raw: {
                        ...node,
                        id: node.id || `${kind}-${fullPath}-${index}`,
                        full_path: fullPath
                    }
                }
            })
        },
        normalizeId3Entries(list) {
            return list.map((item, index) => {
                const fullPath = item.play_path || item.full_path || item.parent_path || this.filePath
                return {
                    id: item.id || `id3-${item.filename || item.title || index}-${index}`,
                    kind: 'audio',
                    trackId: item.id || null,
                    title: item.title || item.filename || '未命名',
                    fileName: item.filename || item.title || '未命名',
                    fullPath,
                    updatedAt: item.year ? `${item.year}` : '-',
                    sizeLabel: item.size || '-',
                    status: item.lyrics ? '有歌词' : '无歌词',
                    statusTone: item.lyrics ? 'success' : 'warning',
                    typeLabel: item.artist || '-',
                    artist: item.artist || '-',
                    album: item.album || '-',
                    year: item.year || '-',
                    genre: item.genre || '',
                    lyrics: item.lyrics || '',
                    artwork: item.artwork || '',
                    raw: {
                        ...item,
                        id: item.id || `id3-${item.filename || item.title || index}-${index}`
                    }
                }
            })
        },
        resolveKind(node) {
            const icon = node.icon || ''
            const name = String(node.name || '').toLowerCase()
            const ext = name.includes('.') ? name.split('.').pop() : ''

            if (icon === 'icon-folder' || Array.isArray(node.children)) {
                return 'folder'
            }
            if (icon === 'icon-image' || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) {
                return 'image'
            }
            if (['flac', 'mp3', 'm4a', 'wav', 'aac', 'ogg', 'ape', 'opus', 'dts', 'cue', 'strm'].includes(ext)) {
                return 'audio'
            }
            if (['txt', 'md', 'doc', 'docx', 'pdf', 'json', 'lrc'].includes(ext)) {
                return 'document'
            }
            return 'file'
        },
        resolveTypeLabel(node, kind) {
            if (kind === 'folder') {
                return '文件夹'
            }
            if (kind === 'image') {
                return '图片'
            }
            if (kind === 'audio') {
                return '音频'
            }
            if (kind === 'document') {
                return '文档'
            }
            return '文件'
        },
        resolveStatus(node, kind) {
            if (kind === 'folder') {
                return '文件夹'
            }
            if (node.state === 'success') {
                return '已完成'
            }
            if (node.state === 'failed') {
                return '失败'
            }
            if (kind === 'image') {
                return '图片文件'
            }
            if (kind === 'audio') {
                return '音频文件'
            }
            if (kind === 'document') {
                return '文本文档'
            }
            return '普通文件'
        },
        resolveStatusTone(node, kind) {
            if (kind === 'folder') {
                return 'primary'
            }
            if (node.state === 'success') {
                return 'success'
            }
            if (node.state === 'failed') {
                return 'danger'
            }
            if (kind === 'audio') {
                return 'warning'
            }
            return 'neutral'
        },
        formatSizeLabel(node, kind) {
            if (kind === 'folder') {
                if (node.children_count != null) {
                    return `${node.children_count} 项`
                }
                if (Array.isArray(node.children)) {
                    return `${node.children.length} 项`
                }
                if (node.size) {
                    return String(node.size)
                }
                return ''
            }

            if (typeof node.size === 'number') {
                return this.formatBytes(node.size)
            }
            if (typeof node.size === 'string' && node.size) {
                return node.size
            }
            return '-'
        },
        formatBytes(size) {
            if (!size || Number.isNaN(size)) {
                return '-'
            }
            const units = ['B', 'KB', 'MB', 'GB', 'TB']
            let value = Number(size)
            let unitIndex = 0
            while (value >= 1024 && unitIndex < units.length - 1) {
                value /= 1024
                unitIndex += 1
            }
            return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`
        },
        normalizeSelectionPayload(item) {
            if (!item) {
                return null
            }

            const raw = item.raw ? { ...item.raw } : { ...item }
            const kind = item.kind || raw.kind || this.resolveKind(raw)
            const normalizedName = raw.name || raw.filename || item.fileName || item.title || ''

            return {
                ...raw,
                id: raw.id || item.id,
                name: normalizedName,
                filename: raw.filename || normalizedName,
                icon: kind === 'folder' ? 'icon-folder' : (kind === 'image' ? 'icon-image' : (raw.icon || '')),
                full_path: raw.full_path || item.fullPath || this.filePath,
                parent_path: raw.parent_path || this.filePath,
            }
        },
        initSearchState() {
            const currentSearchWord = this.searchWord || ''
            const matchedFilter = this.filterOptions.find((item) => item.id === currentSearchWord)

            if (matchedFilter) {
                this.selectedFilterId = matchedFilter.id
                this.localSearchWord = ''
                return
            }

            this.selectedFilterId = null
            this.localSearchWord = currentSearchWord
        },
        getOptionLabel(options, value) {
            const matched = (options || []).find((item) => String(item.id) === String(value))
            return matched ? matched.name : value
        },
        getEffectiveSearchWord() {
            if (this.selectedFilterId) {
                return this.selectedFilterId
            }
            return this.localSearchWord.trim()
        },
        handleSearchInput() {
            if (this.viewMode === 'card' && this.localSearchWord && this.selectedFilterId) {
                this.selectedFilterId = null
            }
        },
        applySearch() {
            const nextSearchWord = this.localSearchWord.trim()

            if (nextSearchWord.startsWith('path:')) {
                const nextPath = nextSearchWord.replace(/^path:/, '').trim()
                if (nextPath) {
                    this.selectedFilterId = null
                    this.localSearchWord = ''
                    this.$store.commit('setSearchWord', '')
                    this.navigateToPath(nextPath)
                }
                return
            }

            const committedSearchWord = this.viewMode === 'list'
                ? nextSearchWord
                : this.getEffectiveSearchWord()

            this.$store.commit('setSearchWord', committedSearchWord)
            this.fetchCurrentView()
        },
        clearSearch() {
            this.localSearchWord = ''
            this.selectedFilterId = null
            this.$store.commit('setSearchWord', '')
            this.fetchCurrentView()
        },
        clearSelectedFilter() {
            this.selectedFilterId = null
            this.$store.commit('setSearchWord', this.localSearchWord.trim())
            this.fetchCurrentView()
        },
        selectFilterOption(option) {
            if (!option?.id) {
                return
            }

            this.selectedFilterId = option.id
            if (this.viewMode === 'card') {
                this.localSearchWord = ''
                this.$store.commit('setSearchWord', option.id)
            }
            this.fetchCurrentView()
        },
        handleMetaFilterChange(type, value) {
            if (type === 'artist') {
                this.selectedArtistFilter = value
            } else if (type === 'album') {
                this.selectedAlbumFilter = value
            } else if (type === 'year') {
                this.selectedYearFilter = value
            } else if (type === 'artwork') {
                this.selectedArtworkFilter = value
            }

            if (this.viewMode === 'list') {
                this.fetchCurrentView()
            }
        },
        clearMetaFilter(type) {
            if (type === 'artist') {
                this.selectedArtistFilter = ''
            } else if (type === 'album') {
                this.selectedAlbumFilter = ''
            } else if (type === 'year') {
                this.selectedYearFilter = ''
            } else if (type === 'artwork') {
                this.selectedArtworkFilter = ''
            }

            if (this.viewMode === 'list') {
                this.fetchCurrentView()
            }
        },
        handleSelectionPointerDown(item, event) {
            if (!item || event.button !== 0) {
                return
            }

            event.preventDefault()
            event.stopPropagation()

            this.isDragSelecting = true
            this.dragStartId = item.id
            this.dragLastId = item.id
            this.dragSelectionMode = this.isSelected(item.id) ? 'deselect' : 'select'
            this.dragInitialCheckedIds = [...this.checkedIds]
            this.dragInitialCheckedData = [...this.checkedData]

            this.applyDragSelectionByTargetId(item.id)
        },
        handleSelectionPointerEnter(item) {
            if (!this.isDragSelecting || !item || item.id === this.dragLastId) {
                return
            }

            this.dragLastId = item.id
            this.applyDragSelectionByTargetId(item.id)
        },
        finishDragSelection() {
            if (!this.isDragSelecting) {
                return
            }

            this.resetDragSelectionState()
        },
        applyDragSelectionByTargetId(targetId) {
            const rangeIds = this.getDragRangeIds(targetId)
            if (rangeIds.length === 0) {
                return
            }

            this.dragPreviewIds = rangeIds

            const nextIdsSet = new Set(this.dragInitialCheckedIds)
            const nextDataMap = new Map(this.dragInitialCheckedData.map((entry) => [entry.id, entry]))
            const visibleEntryMap = new Map(this.currentViewEntries.map((entry) => [entry.id, entry.raw]))

            rangeIds.forEach((id) => {
                if (this.dragSelectionMode === 'deselect') {
                    nextIdsSet.delete(id)
                    nextDataMap.delete(id)
                    return
                }

                nextIdsSet.add(id)
                if (visibleEntryMap.has(id)) {
                    const selectionPayload = this.normalizeSelectionPayload(visibleEntryMap.get(id))
                    if (selectionPayload) {
                        nextDataMap.set(id, selectionPayload)
                    }
                }
            })

            this.checkedIds = [...nextIdsSet]
            this.checkedData = [...nextDataMap.values()].filter((entry) => nextIdsSet.has(entry.id))
        },
        getDragRangeIds(targetId) {
            const entries = this.currentViewEntries || []
            const startIndex = entries.findIndex((entry) => entry.id === this.dragStartId)
            const targetIndex = entries.findIndex((entry) => entry.id === targetId)

            if (startIndex === -1 || targetIndex === -1) {
                return []
            }

            const rangeStart = Math.min(startIndex, targetIndex)
            const rangeEnd = Math.max(startIndex, targetIndex)
            return entries.slice(rangeStart, rangeEnd + 1).map((entry) => entry.id)
        },
        changeSorted(fieldId) {
            if (!fieldId) {
                return
            }

            const positiveField = fieldId
            const negativeField = `-${fieldId}`
            const nextSortedFields = Array.isArray(this.sortedField) ? [...this.sortedField] : []
            const positiveIndex = nextSortedFields.indexOf(positiveField)
            const negativeIndex = nextSortedFields.indexOf(negativeField)

            if (positiveIndex > -1) {
                nextSortedFields.splice(positiveIndex, 1)
                nextSortedFields.push(negativeField)
            } else if (negativeIndex > -1) {
                nextSortedFields.splice(negativeIndex, 1)
            } else {
                nextSortedFields.push(positiveField)
            }

            this.$store.commit('setSortedField', nextSortedFields)
            this.fetchCurrentView()
        },
        getSortStatus(fieldId) {
            const positiveField = fieldId
            const negativeField = `-${fieldId}`

            if (this.sortedField.includes(positiveField)) {
                return 'asc'
            }
            if (this.sortedField.includes(negativeField)) {
                return 'desc'
            }
            return null
        },
        clearSortedFields() {
            this.$store.commit('setSortedField', [])
            this.fetchCurrentView()
        },
        getListItemRaw(item) {
            return item?.raw || {}
        },
        getRawFieldValue(raw, keys = []) {
            for (const key of keys) {
                const value = raw?.[key]
                if (value !== undefined && value !== null && value !== '') {
                    return value
                }
            }
            return ''
        },
        formatDurationDisplay(value) {
            if (value === undefined || value === null || value === '') {
                return '-'
            }

            if (typeof value === 'string' && value.includes(':')) {
                return value
            }

            const totalSeconds = Number(value)
            if (Number.isNaN(totalSeconds)) {
                return String(value)
            }

            const roundedSeconds = Math.max(0, Math.round(totalSeconds))
            const hours = Math.floor(roundedSeconds / 3600)
            const minutes = Math.floor((roundedSeconds % 3600) / 60)
            const seconds = roundedSeconds % 60

            if (hours > 0) {
                return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
            }
            return `${minutes}:${String(seconds).padStart(2, '0')}`
        },
        formatTrackPairValue(currentValue, totalValue) {
            if (!currentValue && !totalValue) {
                return '-'
            }
            if (currentValue && totalValue) {
                return `${currentValue}/${totalValue}`
            }
            return String(currentValue || totalValue)
        },
        hasListColumnValue(item, fieldId) {
            const value = this.getListColumnDisplayValue(item, fieldId, { preserveBoolean: true })
            if (typeof value === 'boolean') {
                return value
            }
            return value !== '-' && value !== ''
        },
        getListColumnDisplayValue(item, fieldId, options = {}) {
            const { preserveBoolean = false } = options
            const raw = this.getListItemRaw(item)

            const fieldValueMap = {
                title: this.getRawFieldValue(raw, ['title']) || item?.title || '-',
                filename: this.getRawFieldValue(raw, ['filename', 'name']) || item?.fileName || '-',
                artist: this.getRawFieldValue(raw, ['artist']) || item?.artist || '-',
                album: this.getRawFieldValue(raw, ['album']) || item?.album || '-',
                album_type: this.getRawFieldValue(raw, ['album_type']) || '-',
                albumartist: this.getRawFieldValue(raw, ['albumartist', 'album_artist']) || '-',
                discnumber: this.formatTrackPairValue(
                    this.getRawFieldValue(raw, ['discnumber']),
                    this.getRawFieldValue(raw, ['totaldiscs'])
                ),
                tracknumber: this.formatTrackPairValue(
                    this.getRawFieldValue(raw, ['tracknumber']),
                    this.getRawFieldValue(raw, ['totaltracks'])
                ),
                totaltracks: this.getRawFieldValue(raw, ['totaltracks']) || '-',
                totaldiscs: this.getRawFieldValue(raw, ['totaldiscs']) || '-',
                genre: this.getRawFieldValue(raw, ['genre']) || item?.genre || '-',
                year: this.getRawFieldValue(raw, ['year']) || item?.year || '-',
                lyrics: Boolean(this.getRawFieldValue(raw, ['lyrics'])),
                lyrics_file: Boolean(this.getRawFieldValue(raw, ['lyrics_file'])),
                comment: this.getRawFieldValue(raw, ['comment']) || '-',
                description: this.getRawFieldValue(raw, ['description']) || '-',
                duration: this.formatDurationDisplay(this.getRawFieldValue(raw, ['duration'])),
                size: (() => {
                    const rawSize = this.getRawFieldValue(raw, ['size'])
                    if (typeof rawSize === 'number' || (typeof rawSize === 'string' && /^\d+(\.\d+)?$/.test(rawSize))) {
                        return this.formatBytes(rawSize)
                    }
                    return rawSize || item?.sizeLabel || '-'
                })(),
                bit_rate: this.getRawFieldValue(raw, ['bit_rate', 'bitrate']) || '-',
                sample_rate: this.getRawFieldValue(raw, ['sample_rate']) || '-',
                channels: this.getRawFieldValue(raw, ['channels']) || '-',
                bit_depth: this.getRawFieldValue(raw, ['bit_depth']) || '-',
                language: this.getRawFieldValue(raw, ['language']) || '-',
                composer: this.getRawFieldValue(raw, ['composer']) || '-',
                lyricist: this.getRawFieldValue(raw, ['lyricist']) || '-',
                replaygain_track_gain: this.getRawFieldValue(raw, ['replaygain_track_gain']) || '-',
                replaygain_track_peak: this.getRawFieldValue(raw, ['replaygain_track_peak']) || '-',
                replaygain_album_gain: this.getRawFieldValue(raw, ['replaygain_album_gain']) || '-',
                replaygain_album_peak: this.getRawFieldValue(raw, ['replaygain_album_peak']) || '-',
                type: item?.typeLabel || '-',
                updatedAt: item?.updatedAt || this.getRawFieldValue(raw, ['update_time', 'updated_at', 'modify_time']) || '-',
                status: item?.status || '-',
            }

            const value = fieldValueMap[fieldId]
            if (preserveBoolean) {
                return value
            }
            return value === undefined || value === null || value === '' ? '-' : value
        },
        persistVisibleListColumns() {
            localStorage.setItem(
                HOME_CLOUD_LIST_FIELDS_STORAGE_KEY,
                JSON.stringify(this.visibleHomeCloudListFields)
            )
        },
        isListColumnVisible(fieldId) {
            return this.visibleHomeCloudListFields.includes(fieldId)
        },
        toggleListColumn(fieldId) {
            if (!fieldId) {
                return
            }

            if (this.isListColumnVisible(fieldId)) {
                if (this.visibleHomeCloudListFields.length === 1) {
                    return
                }
                this.visibleHomeCloudListFields = this.visibleHomeCloudListFields.filter((item) => item !== fieldId)
            } else {
                const nextFieldSet = new Set(this.visibleHomeCloudListFields)
                nextFieldSet.add(fieldId)
                this.visibleHomeCloudListFields = this.homeCloudListFieldOptions
                    .map((item) => item.id)
                    .filter((item) => nextFieldSet.has(item))
            }

            this.persistVisibleListColumns()
        },
        resetListColumns() {
            this.visibleHomeCloudListFields = [...HOME_CLOUD_DEFAULT_LIST_FIELDS]
            this.persistVisibleListColumns()
        },
        initLoadMoreObserver() {
            if (typeof IntersectionObserver === 'undefined') {
                return
            }

            this.loadMoreObserver = new IntersectionObserver((entries) => {
                const [entry] = entries || []
                if (!entry || !entry.isIntersecting) {
                    return
                }
                this.handleLoadMore()
            }, {
                root: null,
                rootMargin: '0px 0px 240px 0px',
                threshold: 0
            })

            this.observeLoadMoreSentinel()
        },
        observeLoadMoreSentinel() {
            if (!this.loadMoreObserver) {
                return
            }

            this.loadMoreObserver.disconnect()
            if (!this.hasMoreData || this.isLoading || this.isLoadingMore) {
                return
            }

            const sentinel = this.$refs.loadMoreSentinel
            if (sentinel) {
                this.loadMoreObserver.observe(sentinel)
            }
        },
        disconnectLoadMoreObserver() {
            if (this.loadMoreObserver) {
                this.loadMoreObserver.disconnect()
                this.loadMoreObserver = null
            }
        },
        async handleLoadMore() {
            if (this.isLoadingMore || !this.hasMoreData || !this.lastRequestParams) {
                return
            }

            this.isLoadingMore = true
            this.currentPage += 1

            try {
                const res = await this.$api.Task.directoryQuery({
                    ...this.lastRequestParams,
                    cursor: this.nextCursor,
                })

                if (!res.result) {
                    throw new Error(res.message || '加载更多失败')
                }

                const responseData = res.data
                const list = Array.isArray(responseData?.items) ? responseData.items : []
                const nextEntries = this.normalizeDirectoryEntries(list)
                this.fileEntries = [...this.fileEntries, ...nextEntries]
                this.listEntries = [...this.listEntries, ...nextEntries]
                this.totalCount = responseData?.total || this.totalCount
                this.hasMoreData = Boolean(responseData?.has_more)
                this.nextCursor = responseData?.next_cursor || null
                this.updateUniqueOptions(list)
                this.$nextTick(() => {
                    this.observeLoadMoreSentinel()
                })
            } catch (error) {
                this.currentPage = Math.max(1, this.currentPage - 1)
                this.$myMsg.notify({
                    title: this.$tt('加载失败'),
                    content: error.message || this.$tt('网络错误'),
                    type: 'error',
                    time: 2000,
                })
            } finally {
                this.isLoadingMore = false
                this.$nextTick(() => {
                    this.observeLoadMoreSentinel()
                })
            }
        },
        handlePageSizeChange(newPageSize) {
            if (!newPageSize || newPageSize === this.pageSize) {
                return
            }

            this.pageSize = newPageSize
            localStorage.setItem('pageSize', String(newPageSize))
            this.fetchCurrentView()
        },
        isSelected(id) {
            return this.checkedIds.includes(id)
        },
        isInDragPreview(id) {
            return this.isDragSelecting && this.dragPreviewIds.includes(id)
        },
        toggleSelect(item) {
            if (!item) {
                return
            }

            if (this.isSelected(item.id)) {
                this.checkedIds = this.checkedIds.filter((id) => id !== item.id)
                this.checkedData = this.checkedData.filter((entry) => entry.id !== item.id)
                return
            }

            this.checkedIds = [...this.checkedIds, item.id]
            const selectionPayload = this.normalizeSelectionPayload(item)
            this.checkedData = selectionPayload ? [...this.checkedData, selectionPayload] : [...this.checkedData]
        },
        toggleAllSelection() {
            if (this.allSelected) {
                const visibleIds = new Set(this.currentViewEntries.map((item) => item.id))
                this.checkedIds = this.checkedIds.filter((id) => !visibleIds.has(id))
                this.checkedData = this.checkedData.filter((entry) => !visibleIds.has(entry.id))
                return
            }

            const nextIds = new Set(this.checkedIds)
            const nextData = [...this.checkedData]
            this.currentViewEntries.forEach((item) => {
                if (!nextIds.has(item.id)) {
                    nextIds.add(item.id)
                    const selectionPayload = this.normalizeSelectionPayload(item)
                    if (selectionPayload) {
                        nextData.push(selectionPayload)
                    }
                }
            })
            this.checkedIds = [...nextIds]
            this.checkedData = nextData
        },
        handlePrimaryAction(item) {
            if (!item) {
                return
            }
            if (item.kind === 'audio') {
                this.openInspector(item)
                return
            }
            this.handleRowAction(item)
        },
        handleRowAction(item) {
            if (!item) {
                return
            }
            if (item.kind === 'folder') {
                this.navigateToPath(item.fullPath)
                return
            }
            if (item.kind === 'audio') {
                this.openInspector(item)
                return
            }
            if (item.kind === 'image') {
                this.openImagePreview(item)
                return
            }
            const targetPath = item?.raw?.parent_path || this.getDirectoryName(item.fullPath)
            if (targetPath) {
                this.navigateToPath(targetPath)
            }
        },
        getRowActionLabel(item) {
            if (!item) {
                return '定位'
            }
            if (item.kind === 'folder') {
                return '打开'
            }
            if (item.kind === 'audio') {
                return '详情'
            }
            if (item.kind === 'image') {
                return '预览'
            }
            return '定位'
        },
        isPlayableAudioItem(item) {
            if (!item || item.kind !== 'audio') {
                return false
            }
            const trackId = item.trackId || item?.raw?.track_id || item?.raw?.id
            return Boolean(trackId && String(trackId).startsWith('folder-') === false)
        },
        async playAudioItem(item) {
            const trackId = item?.trackId || item?.raw?.track_id
            if (!trackId) {
                this.openInspector(item)
                return
            }

            try {
                const currentTrack = this.$store.getters.track
                if (currentTrack && String(currentTrack.id) === String(trackId)) {
                    await this.$store.dispatch('playPause')
                    return
                }

                const song = await this.$api.subsonic.getSong(trackId)
                await this.$store.dispatch('playTrackList', {
                    tracks: [song],
                    index: 0
                })
            } catch (error) {
                this.$myMsg.notify({
                    title: this.$tt('播放失败'),
                    content: error.message || this.$tt('网络错误'),
                    type: 'error',
                    time: 3000
                })
            }
        },
        async openImagePreview(item) {
            const imagePath = item?.raw?.full_path || item?.fullPath
            if (!imagePath) {
                return
            }

            this.isImagePreviewOpen = true
            this.isImageLoading = true
            this.previewImageUrl = ''
            this.previewImageTitle = item.title || item.fileName || '图片预览'

            try {
                const response = await this.$api.Task.getImage({ file_path: imagePath })
                if (!response.result || !response.data?.image_data) {
                    throw new Error(response.message || '图片加载失败')
                }
                const mimeType = this.getImageMimeType(item)
                this.previewImageUrl = `data:${mimeType};base64,${response.data.image_data}`
            } catch (error) {
                this.closeImagePreview()
                this.$myMsg.notify({
                    title: this.$tt('图片加载失败'),
                    content: error.message || this.$tt('网络错误'),
                    type: 'error',
                    time: 3000,
                })
            } finally {
                this.isImageLoading = false
            }
        },
        closeImagePreview() {
            this.isImagePreviewOpen = false
            this.isImageLoading = false
            this.previewImageUrl = ''
            this.previewImageTitle = ''
        },
        handleGlobalKeydown(event) {
            if (event.key === 'Escape' && this.isImagePreviewOpen) {
                this.closeImagePreview()
            }
        },
        getImageMimeType(item) {
            const name = String(item?.fileName || item?.title || '').toLowerCase()
            const ext = name.includes('.') ? name.split('.').pop() : ''
            const typeMap = {
                jpg: 'image/jpeg',
                jpeg: 'image/jpeg',
                png: 'image/png',
                gif: 'image/gif',
                webp: 'image/webp',
                bmp: 'image/bmp',
                svg: 'image/svg+xml'
            }
            return typeMap[ext] || 'image/jpeg'
        },
        async openInspector(target) {
            const item = typeof target === 'object'
                ? target
                : [...this.fileEntries, ...this.listEntries].find((entry) => entry.id === target)

            if (!item) {
                return
            }

            this.activeItemId = item.id
            this.detailTarget = item
            this.detailLoading = true
            this.detailError = ''
            this.detailMusicInfo = {}

            try {
                const parentPath = this.getParentPathForDetail(item)
                const fileName = this.getFileNameForDetail(item)
                const res = await this.$api.Task.musicId3({
                    file_path: parentPath,
                    file_name: fileName
                })

                if (!res.result) {
                    throw new Error(res.message || '加载详情失败')
                }

                this.detailMusicInfo = {
                    ...res.data
                }
            } catch (error) {
                this.detailError = error.message || '加载详情失败'
            } finally {
                this.detailLoading = false
            }
        },
        closeInspector() {
            this.activeItemId = null
            this.detailTarget = null
            this.detailMusicInfo = {}
            this.detailError = ''
            this.detailLoading = false
            this.resetTransientPanels()
        },
        openBatchAction(sectionKey) {
            if (this.selectedCount === 0) {
                return
            }
            if (this.openDirectBatchAction(sectionKey)) {
                return
            }
            const titleMap = {
                'tag-editor': '批量编辑',
                'file-operations': '整理目录',
                'media-processor': '抓取资源',
                'batch-settings': '更多设置',
                'text-processor': '文本处理',
                'file-indexer': '索引工具'
            }
            this.batchPanelTitle = titleMap[sectionKey] || '批量操作'
            this.batchPanelSection = sectionKey || 'tag-editor'
            this.moreActionsOpen = false
            this.batchPanelOpen = true
        },
        getDirectBatchActionMap() {
            const runner = (operation) => ({
                ref: 'batchOperationRunner',
                open: 'openOperationDrawer',
                close: 'closeOperationDrawer',
                isOpen: 'isOperationDrawerOpen',
                operation
            })
            return {
                'tag-editor': runner('update_tags'),
                'auto-scrape': runner('auto_tag'),
                'background-scrape': runner('background_scrape'),
                'tidy-folder': runner('tidy_folder'),
                'delete-folder': runner('remove_null_folder'),
                'format-music': runner('format_music'),
                'split-cue': runner('split_cue'),
                'import-music': runner('import_music'),
                'duplicate-check': runner('duplicate_check'),
                'replay-gain': runner('replay_gain'),
                'generate-strm': runner('generate_strm'),
                'language-convert': runner('convert_chinese'),
                'split-metadata': runner('split_filename'),
                'replace-text': runner('replace_text'),
                'encoding-fix': runner('encoding_fix'),
                'file-index': runner('file_index'),
                'batch-settings': { ref: 'batchSettings', open: 'openSettingsDrawer', close: 'closeSettingsDrawer', isOpen: 'isSettingsDrawerOpen' }
            }
        },
        getDirectBatchActionTarget(actionKey) {
            return this.getDirectBatchActionMap()[actionKey] || null
        },
        handleQuickActionClick(actionKey) {
            if (actionKey === 'more-actions') {
                this.toggleMoreActions()
                return
            }

            const target = this.getDirectBatchActionTarget(actionKey)
            if (!target) {
                this.openBatchAction(actionKey)
                return
            }

            const componentRef = this.$refs[target.ref]
            const isOpen = componentRef && typeof componentRef[target.isOpen] === 'function'
                ? componentRef[target.isOpen]()
                : false

            if (isOpen) {
                this.closeDirectBatchAction(actionKey)
                this.activeQuickActionKey = ''
                return
            }

            this.closeAllDirectBatchActions(actionKey)
            this.moreActionsOpen = false
            this.batchPanelOpen = false
            this.$nextTick(() => {
                const nextRef = this.$refs[target.ref]
                if (nextRef && typeof nextRef[target.open] === 'function') {
                    nextRef[target.open](target.operation)
                    this.activeQuickActionKey = actionKey
                }
            })
        },
        openDirectBatchAction(actionKey) {
            const target = this.getDirectBatchActionTarget(actionKey)
            if (!target) {
                return false
            }

            this.closeAllDirectBatchActions(actionKey)
            this.moreActionsOpen = false
            this.batchPanelOpen = false
            this.$nextTick(() => {
                const componentRef = this.$refs[target.ref]
                if (componentRef && typeof componentRef[target.open] === 'function') {
                    componentRef[target.open](target.operation)
                }
            })
            return true
        },
        closeDirectBatchAction(actionKey) {
            const target = this.getDirectBatchActionTarget(actionKey)
            if (!target) {
                return
            }

            const componentRef = this.$refs[target.ref]
            if (componentRef && typeof componentRef[target.close] === 'function') {
                componentRef[target.close]()
            }
        },
        closeAllDirectBatchActions(exceptKey = '') {
            const actionMap = this.getDirectBatchActionMap()
            Object.keys(actionMap).forEach((actionKey) => {
                if (exceptKey && actionKey === exceptKey) {
                    return
                }
                this.closeDirectBatchAction(actionKey)
            })
        },
        openManualBatchDrawer() {
            this.moreActionsOpen = false
            this.batchPanelOpen = false
            this.$nextTick(() => {
                const runner = this.$refs.batchOperationRunner
                if (runner && typeof runner.openOperationDrawer === 'function') {
                    runner.openOperationDrawer('update_tags')
                    return
                }

                this.batchPanelTitle = '批量编辑'
                this.batchPanelSection = 'tag-editor'
                this.batchPanelOpen = true
            })
        },
        handleBatchPanelChange(sectionKey) {
            if (sectionKey === 'tag-editor') {
                this.openManualBatchDrawer()
                return
            }
            const titleMap = {
                'tag-editor': '批量编辑',
                'file-operations': '整理目录',
                'media-processor': '抓取资源',
                'batch-settings': '更多设置',
                'text-processor': '文本处理',
                'file-indexer': '索引工具'
            }
            this.batchPanelSection = sectionKey
            this.batchPanelTitle = titleMap[sectionKey] || '批量操作'
        },
        closeBatchPanel() {
            this.batchPanelOpen = false
        },
        toggleMoreActions(forceValue) {
            if (typeof forceValue === 'boolean') {
                this.moreActionsOpen = forceValue
                return
            }
            this.moreActionsOpen = !this.moreActionsOpen
        },
        clearSelection() {
            this.checkedIds = []
            this.checkedData = []
            this.batchPanelOpen = false
            this.moreActionsOpen = false
            this.closeAllDirectBatchActions()
            this.activeQuickActionKey = ''
        },
        retryLoadDetail() {
            if (this.detailTarget) {
                this.openInspector(this.detailTarget)
            }
        },
        getParentPathForDetail(item) {
            if (item?.raw?.parent_path) {
                return item.raw.parent_path
            }
            if (item?.fullPath) {
                return this.getDirectoryName(item.fullPath)
            }
            return this.filePath
        },
        getFileNameForDetail(item) {
            if (item?.raw?.filename) {
                return item.raw.filename
            }
            if (item?.fileName) {
                return item.fileName
            }
            return item?.title || ''
        },
        async handleClick() {
            const reopenTarget = this.buildDetailTargetFromMusicInfo(this.detailMusicInfo)
            await this.fetchCurrentView(true, {
                preserveContext: true,
                preserveSelection: true
            })

            if (reopenTarget) {
                await this.openInspector(reopenTarget)
            }
        },
        handleCopy(key, value, callback) {
            if (!this.detailMusicInfo || Object.keys(this.detailMusicInfo).length === 0) {
                return
            }

            if (key === 'lyric') {
                const resource = localStorage.getItem('resource') || 'netease'
                const currentResource = resource !== 'smart_tag' ? resource : value.resource

                this.$api.Task.fetchLyric({ song_id: value.id, resource: currentResource }).then((res) => {
                    if (res.result && res.data) {
                        this.detailMusicInfo.lyrics = res.data
                        this.handleCopy('composer', this.detailMusicInfo.lyrics)
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('未找到歌词！'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                }).finally(() => {
                    if (typeof callback === 'function') {
                        callback()
                    }
                })
                return
            }

            if (key === 'composer') {
                this.$api.Task.extractLyric({ lyc: value }).then((res) => {
                    if (res.result && res.data) {
                        this.detailMusicInfo.composer = res.data.composer
                        this.detailMusicInfo.lyricist = res.data.lyricist
                    }
                })
                return
            }

            if (key === 'album_img') {
                this.detailMusicInfo.album_img = ''
                this.detailMusicInfo.artwork = value
                this.detailMusicInfo.files1 = [{
                    name: 'new.png',
                    status: 'done',
                    url: value
                }]
                this.reloadImg = false
                this.$nextTick(() => {
                    this.reloadImg = true
                })
                return
            }

            if (key === 'lyric_tran') {
                this.detailMusicInfo.lyrics = value
                this.showTranslation = false
                return
            }

            if (key === 'artist') {
                this.detailMusicInfo.artist_list = splitArtistText(value, this.$store.state.common.sysDict || {})
            }

            this.$set(this.detailMusicInfo, key, value)
        },
        copyAll(item) {
            this.handleCopy('title', item.name)
            this.handleCopy('year', item.year)
            this.handleCopy('lyric', item)
            this.handleCopy('album', item.album)
            this.handleCopy('artist', item.artist)
            this.handleCopy('album_img', item.album_img)
            this.handleCopy('tracknumber', item.tracknumber)
            this.handleCopy('discnumber', item.discnumber)
            this.handleCopy('totaltracks', item.totaltracks)
            this.handleCopy('totaldiscs', item.totaldiscs)
        },
        toggleLock(mode = 'drawer') {
            if (!this.detailMusicInfo?.title) {
                this.$myMsg.notify({
                    title: this.$tt('标题不能为空！'),
                    type: 'error',
                    time: 2000,
                })
                return
            }

            this.showTranslation = false
            this.showLyricsTimeline = false
            this.isShowResourceId3 = mode !== 'page'
            this.fullPath = this.detailMusicInfo.path || this.joinPath(this.filePath, this.detailMusicInfo.filename || this.detailMusicInfo.title)
            this.section3Loading = true

            this.$api.Task.fetchId3Title({
                title: this.detailMusicInfo.title,
                artist: this.detailMusicInfo.artist,
                album: this.detailMusicInfo.album,
                full_path: this.fullPath,
                resource: localStorage.getItem('resource') || 'netease'
            }).then((res) => {
                this.SongList = res.data || []
            }).finally(() => {
                this.section3Loading = false
            })
        },
        translation() {
            if (!this.detailMusicInfo?.lyrics) {
                this.$myMsg.notify({
                    title: this.$tt('歌词不能为空！'),
                    type: 'error',
                    time: 2000,
                })
                return
            }

            this.isShowResourceId3 = false
            this.showTranslation = true
            this.$api.Task.translationLyc({
                lyc: this.detailMusicInfo.lyrics
            }).then((res) => {
                this.translationText = res.data || ''
            })
        },
        showLyricsTimelineModal(playPath, lyrics) {
            this.showLyricsTimeline = true
            this.playPath = playPath
            this.rawLyrics = lyrics
        },
        handleSaveLyrics(lyrics) {
            if (this.detailMusicInfo) {
                this.detailMusicInfo.lyrics = lyrics
            }
        },
        handleShowVersionHistory(trackInfo) {
            if (!trackInfo) {
                return
            }

            this.versionTrackInfo = trackInfo
            this.showVersionHistory = true

            this.$nextTick(() => {
                if (this.$refs.versionHistoryComponent) {
                    this.$refs.versionHistoryComponent.open(
                        trackInfo.id,
                        trackInfo.title,
                        trackInfo.path
                    )
                }
            })
        },
        async handleVersionRestored(version) {
            this.$myMsg.notify({
                title: this.$tt('版本回退成功'),
                content: this.$tt('已回退到版本 v{version}', { version: version.version_number }),
                type: 'success',
                time: 2000,
            })

            this.showVersionHistory = false
            await this.handleClick()
        },
        buildDetailTargetFromMusicInfo(musicInfo) {
            if (!musicInfo) {
                return null
            }

            const fullPath = musicInfo.path || this.joinPath(this.filePath, musicInfo.filename || musicInfo.title || '')
            const fileName = musicInfo.filename || this.getBaseName(fullPath)

            if (!fullPath || !fileName) {
                return null
            }

            return {
                id: musicInfo.id || this.activeItemId || `detail-${fullPath}`,
                fullPath,
                fileName,
                title: musicInfo.title || fileName,
                raw: {
                    parent_path: this.getDirectoryName(fullPath),
                    filename: fileName
                }
            }
        },
        joinPath(basePath, fileName) {
            if (!basePath) {
                return fileName
            }
            if (!fileName) {
                return basePath
            }
            return basePath.endsWith('/') ? `${basePath}${fileName}` : `${basePath}/${fileName}`
        },
        syncRootPath(path) {
            const nextPath = path || ''
            const storedRootPath = this.$store.state.common.homeCloudRootPath || ''

            if (storedRootPath && nextPath.startsWith(storedRootPath)) {
                this.rootPath = storedRootPath
                return
            }

            this.rootPath = nextPath
            this.$store.commit('setHomeCloudRootPath', nextPath)
        },
        navigateToPath(path) {
            if (!path || path === this.filePath) {
                return
            }
            this.filePath = path
        },
        goBack() {
            const previous = this.backPath(this.filePath)
            if (previous && previous !== this.filePath) {
                this.filePath = previous
            }
        },
        getBaseName(path) {
            if (!path) return ''
            const normalized = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path
            const parts = normalized.split('/').filter(Boolean)
            return parts[parts.length - 1] || normalized
        },
        getDirectoryName(path) {
            if (!path) return ''
            const normalized = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path
            const index = normalized.lastIndexOf('/')
            if (index <= 0) {
                return normalized
            }
            return normalized.slice(0, index)
        },
        backPath(path) {
            if (!path) {
                return path
            }
            if (this.rootPath && path === this.rootPath) {
                return path
            }
            const normalized = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path
            const index = normalized.lastIndexOf('/')
            if (index <= 0) {
                return normalized
            }
            const parent = normalized.slice(0, index)
            if (this.rootPath && parent.length < this.rootPath.length && normalized.startsWith(this.rootPath)) {
                return this.rootPath
            }
            return parent || normalized
        },
    }
}
</script>

<style scoped>
.cloud-home {
    min-height: 0;
    border-radius: 1rem;
    background:
        linear-gradient(90deg, color-mix(in oklch, oklch(var(--b1)) 50%, transparent), transparent 42%),
        linear-gradient(180deg, transparent, color-mix(in oklch, oklch(var(--b3)) 42%, transparent)),
        radial-gradient(circle at 12% 8%, color-mix(in srgb, oklch(var(--p)) 16%, transparent), transparent 28rem),
        radial-gradient(circle at 88% 4%, color-mix(in srgb, oklch(var(--a)) 12%, transparent), transparent 26rem),
        oklch(var(--b2));
}

.cloud-home-shell {
    width: 100%;
    max-width: 1480px;
    margin-left: auto;
    margin-right: auto;
}

.cloud-workbar,
.cloud-workspace {
    border-color: color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    background: color-mix(in oklch, oklch(var(--b1)) 86%, transparent);
    box-shadow: 0 18px 50px color-mix(in oklch, oklch(var(--bc)) 8%, transparent);
}

.cloud-workbar {
    backdrop-filter: blur(14px);
}

.cloud-workspace {
    backdrop-filter: blur(10px);
}

.cloud-tabs,
.cloud-chip,
.cloud-pagination,
.cloud-list-head,
.cloud-tool-button {
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 9%, transparent);
    background: color-mix(in oklch, oklch(var(--b2)) 72%, transparent);
}

.cloud-search {
    border-color: color-mix(in oklch, oklch(var(--bc)) 9%, transparent);
    background: color-mix(in oklch, oklch(var(--b1)) 78%, transparent);
}

.cloud-tool-button:hover,
.cloud-icon-button:hover {
    border-color: color-mix(in oklch, oklch(var(--p)) 28%, transparent);
    background: color-mix(in oklch, oklch(var(--p)) 8%, oklch(var(--b1)));
}

.cloud-state-panel {
    border-radius: 16px;
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 9%, transparent);
    background: color-mix(in oklch, oklch(var(--b2)) 52%, transparent);
}

.cloud-card-grid {
    align-content: start;
}

.cloud-file-card {
    min-height: 138px;
    border-radius: 16px;
    padding: 12px 8px 10px;
}

.cloud-file-card:hover {
    background: color-mix(in oklch, oklch(var(--b2)) 56%, transparent);
}

.cloud-content {
    scrollbar-width: thin;
    overscroll-behavior: contain;
}

.cloud-list-head {
    display: none;
}

.cloud-column-picker-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    max-height: min(60vh, 440px);
    overflow-y: auto;
}

.cloud-list-scroll {
    scrollbar-width: thin;
}

.cloud-list-table {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: 100%;
}

.cloud-list-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    width: max-content;
    min-width: 100%;
    background: color-mix(in oklch, oklch(var(--b1)) 70%, transparent);
}

.cloud-list-row:hover {
    border-color: color-mix(in oklch, oklch(var(--p)) 18%, transparent);
    background: color-mix(in oklch, oklch(var(--p)) 5%, oklch(var(--b1)));
}

.cloud-check,
.cloud-floating-check {
    position: relative;
    display: inline-flex;
    width: 20px;
    height: 20px;
    cursor: pointer;
}

.cloud-floating-check {
    position: absolute;
    left: 8px;
    top: 0;
    z-index: 1;
}

.cloud-check input,
.cloud-floating-check input {
    position: absolute;
    inset: 0;
    opacity: 0;
}

.cloud-check span,
.cloud-floating-check span {
    width: 20px;
    height: 20px;
    border-radius: 7px;
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 18%, transparent);
    background: color-mix(in oklch, oklch(var(--b1)) 92%, transparent);
    box-shadow: 0 2px 10px color-mix(in oklch, oklch(var(--bc)) 8%, transparent);
}

.cloud-check input:checked + span,
.cloud-floating-check input:checked + span {
    border-color: oklch(var(--p));
    background: oklch(var(--p));
    box-shadow: inset 0 0 0 4px color-mix(in oklch, oklch(var(--b1)) 94%, transparent);
}

.cloud-drawer-fade-enter,
.cloud-drawer-fade-leave-to,
.cloud-batch-fade-enter,
.cloud-batch-fade-leave-to {
    opacity: 0;
}

.cloud-drawer-slide-enter,
.cloud-drawer-slide-leave-to {
    opacity: 0;
    transform: translateX(24px);
}

.cloud-side-drawer {
    isolation: isolate;
}

.cloud-tag-editor-host {
    position: fixed;
    top: 0;
    left: -9999px;
    width: 0;
    height: 0;
    z-index: 80;
}

.cloud-tag-editor-host /deep/ .drawer-side {
    z-index: 80 !important;
}

.cloud-quick-actions {
    scrollbar-width: none;
}

.cloud-quick-actions::-webkit-scrollbar {
    display: none;
}

@media (min-width: 721px) {
    .cloud-list-head {
        display: grid;
        grid-template-columns: var(--cloud-list-grid-template);
        width: max-content;
        min-width: 100%;
    }

    .cloud-list-row {
        grid-template-columns: var(--cloud-list-grid-template);
    }
}

@media (min-width: 1100px) {
    .cloud-column-picker-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (prefers-reduced-motion: no-preference) {
    .cloud-file-card,
    .cloud-tool-button,
    .cloud-icon-button,
    .cloud-list-row {
        transition: background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
    }

    .cloud-file-card:hover {
        transform: translateY(-1px);
    }

    .cloud-drawer-fade-enter-active,
    .cloud-drawer-fade-leave-active,
    .cloud-batch-fade-enter-active,
    .cloud-batch-fade-leave-active {
        transition: opacity 0.2s ease;
    }

    .cloud-drawer-slide-enter-active,
    .cloud-drawer-slide-leave-active {
        transition: transform 0.24s ease, opacity 0.24s ease;
    }
}
</style>
